import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
  Add20Regular,
  Search20Regular,
  Edit20Regular,
  Delete20Regular,
  DocumentPdf20Regular,
  Receipt20Regular,
  ArrowUpload20Regular,
  ArrowDownload20Regular,
  DocumentTable20Regular,
  ArrowSync20Regular,
  Checkmark20Regular,
  CheckmarkCircle20Regular,
  ChevronDown20Regular,
  ErrorCircle20Regular,
  Dismiss20Regular,
  Building20Regular,
  Warning20Regular,
  Eye20Regular,
} from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader";
import { getCompanies, type Company } from "../../companies/companiesApi";
import {
  getEntityDocuments,
  uploadEntityDocument,
  deleteEntityDocument,
  getEntityDocumentFileUrl,
  fetchEntityDocumentFile,
  type EntityDocumentDto,
} from "../../documents/documentsApi";
import { Tooltip } from "../../components/Tooltip";
import { DocumentViewer, type ViewerDocument } from "../../components/DocumentViewer";
import { APP_LOCALE } from "../../config/locale";
import {
  ApiError,
  createInvoice,
  createSupplier,
  deleteInvoice,
  deleteInvoices,
  validateInvoices,
  downloadInvoiceDocument,
  downloadInvoicePaymentProof,
  exportInvoices,
  extractInvoiceFromDocument,
  fetchInvoiceDocument,
  fetchInvoicePaymentProof,
  fetchPaymentProof,
  getApprovalStatuses,
  getExpenseCategories,
  getInvoiceDocumentStatus,
  getInvoices,
  getSuppliers,
  getWorks,
  imputeInvoiceCost,
  removeInvoiceCostImputation,
  setInvoiceAnnulment,
  setInvoiceSummaryExclusion,
  setInvoicesSummaryExclusion,
  scheduleInvoicePayment,
  setInvoiceApprovalStatus,
  setInvoiceCreditNote,
  updateInvoice,
  completeForecast,
  uploadInvoiceDocument,
  removeInvoiceDocument,
  unmergeForecast,
  uploadInvoicePaymentProof,
  applyPaymentProof,
  matchPaymentProof,
  removeInvoicePaymentProof,
  type PaymentProofInvoice,
  type PaymentProofProposal,
  type ApprovalStatusOption,
  type ExpenseCategoryOption,
  type Invoice,
  type InvoiceAllocationInput,
  getInvoiceFilterOptions,
  reclassifyDocument,
  type AllocationFeedbackResult,
  type InvoiceAllocation,
  type InvoiceExtraction,
  type InvoiceFilterOptions,
  type InvoiceFilters,
  type InvoicePayload,
  type InvoiceTotals,
  type SupplierOption,
  type WorkOption,
  type AttentionSummary,
  type ForecastSuggestionWork,
} from "../../faturas/faturasApi";
import {
  getSubcontractors,
  type Subcontractor,
} from "../../subempreiteiros/subempreiteirosApi";
import { AllocationFeedbackControl } from "./AllocationFeedbackControl";
import { PaymentDateMenu } from "./PaymentDateMenu";
import { createWork } from "../../obras/obrasApi";
import {
  SearchableSelect,
  type SearchableSelectOption,
} from "../../components/SearchableSelect";
import { useLookupOptions } from "../../lookups/useLookupOptions";
import {
  ColumnFilterAmount,
  ColumnFilterDateRange,
  ColumnFilterEmpty,
  ColumnFilterSelect,
  ColumnFilterText,
  SortHeader,
  SrOnly,
  toAmount,
  toLocalDayIso,
  totalsLabel,
} from "../../faturas/ColumnFilters";
import {
  formatArrival,
  rowAmounts,
  rowDescription,
  sliceForWork,
} from "../../faturas/invoiceDisplay";
import {
  SelectionTotalsRow,
  useInvoiceSelection,
  type SelectedInvoiceLine,
} from "../../faturas/InvoiceSelection";

type StructuralFilter = "all" | "structural" | "work";

// Uma linha da repartição por obra no formulário (strings; convertidas ao gravar).
interface AllocationRow {
  id?: number;
  workId: string;
  suggestedWorkName: string;
  amount: string;
  description: string;
  // Linha de custo de estrutura (sem obra): imputa sem gerar custo de obra.
  isStructural: boolean;
  // Origem da atribuição atual (badge informativo; o servidor recalcula ao gravar).
  assignmentSourceCode?: string | null;
  // Modo "criar obra nova" (toggle): ligado por omissão quando o documento refere uma obra que não
  // existe no sistema — criar é a ação mais provável; desligar mostra o select das existentes.
  createMode: boolean;
  newWorkName: string;
  newWorkClientId: string;
  createBusy: boolean;
  createError: string | null;
  // Veredicto 👍/👎 já dado à linha persistida (gravação imediata, fora do Save do formulário).
  feedbackTypeCode?: string | null;
  feedbackErrorDescription?: string | null;
  feedbackCorrectedWorkId?: number | null;
  feedbackAt?: string | null;
}

// Valor especial do select de obra por linha que marca a linha como estrutural (sem obra).
const STRUCTURAL_OPTION = "__structural__";

/** Interruptor com o estilo do resto da app (tokens; sem classes novas — o CSS é pré-compilado). */
function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex items-center gap-2 text-sm text-foreground"
    >
      <span
        style={{
          width: "34px",
          height: "18px",
          borderRadius: "999px",
          backgroundColor: checked ? "var(--primary)" : "var(--border)",
          position: "relative",
          transition: "background-color 120ms",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "2px",
            left: checked ? "18px" : "2px",
            width: "14px",
            height: "14px",
            borderRadius: "999px",
            backgroundColor: "var(--primary-foreground)",
            transition: "left 120ms",
          }}
        />
      </span>
      {label}
    </button>
  );
}

// Aparência do badge de proveniência da atribuição por linha. Cores sólidas via style inline
// (mesma razão do STATUS_META das importações: as variantes de opacidade não estão compiladas).
const ASSIGNMENT_SOURCE_META: Record<string, { label: string; color: string; bg: string }> = {
  fingerprint: { label: "Revisão anterior", color: "#15803d", bg: "rgba(34,197,94,0.14)" },
  "memory-exact": { label: "Aprendido", color: "#15803d", bg: "rgba(34,197,94,0.14)" },
  "memory-similar": { label: "Sugestão (item semelhante)", color: "#b45309", bg: "rgba(245,158,11,0.16)" },
  llm: { label: "Sugestão da IA", color: "#6b7280", bg: "rgba(107,114,128,0.14)" },
  manual: { label: "Manual", color: "#6b7280", bg: "rgba(107,114,128,0.14)" },
  category: { label: "Categoria de estrutura", color: "#15803d", bg: "rgba(34,197,94,0.14)" },
};

/**
 * Rótulo da coluna Obra numa fatura repartida. Uma repartição toda de estrutura (categoria sem
 * obra, como Viaturas ou Estaleiro) não pode dizer "5 obras" — prometia obras que estas linhas
 * nunca vão ter. O número continua a ser o das fatias, que é o que a linha expande.
 */
function splitWorkLabel(invoice: Invoice): string {
  const structuralOnly =
    invoice.allocations.length > 0 &&
    invoice.allocations.every((allocation) => allocation.isStructural);
  if (structuralOnly) return `Estrutura (${invoice.workCount})`;
  return invoice.workCount === 1 ? "1 obra" : `${invoice.workCount} obras`;
}

/**
 * Sub-tipo com auto-sugestão: input de texto livre com painel de sugestões da app (mesmo desenho
 * da pesquisa global) filtrado pelo que se escreve — reutilizar grafias em vez de fragmentar
 * ("Restaurante" vs "Restaurant"). O datalist nativo tinha o painel desenhado pelo browser.
 * A lista sugere, não limita: escrever um sub-tipo novo continua válido.
 */
function SubTypeSuggestField({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  // Distingue "abri o painel a navegar com as setas" de "só passei pelo campo": com o campo
  // vazio, Tab/Enter só aceitam depois de navegar — senão atravessar o formulário injetava
  // o primeiro sub-tipo sem ninguém pedir.
  const [navigated, setNavigated] = useState(false);

  const matches = useMemo(() => {
    const query = value.trim().toLowerCase();
    const pool = query
      ? options.filter((option) => option.toLowerCase().includes(query))
      : options;
    // O valor já escrito por inteiro não é sugestão — sobrava um painel a repetir o campo.
    return pool.filter((option) => option !== value);
  }, [options, value]);

  const active = matches.length > 0 ? Math.min(highlight, matches.length - 1) : -1;

  const close = () => {
    setIsOpen(false);
    setNavigated(false);
  };

  const accept = (option: string) => {
    onChange(option);
    close();
  };

  return (
    <div className="relative flex flex-col gap-1">
      <span className="text-sm text-secondary-text">Sub-tipo</span>
      <input
        type="text"
        value={value}
        maxLength={200}
        placeholder="Texto livre (opcional)"
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
          setHighlight(0);
          setNavigated(false);
        }}
        onFocus={() => setIsOpen(true)}
        // O blur cobre Tab para fora e cliques fora; os cliques no painel não o disparam
        // porque o mousedown lá em baixo é cancelado.
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            close();
            return;
          }
          if (!isOpen || active < 0) return;
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setNavigated(true);
            const delta = event.key === "ArrowDown" ? 1 : matches.length - 1;
            setHighlight((active + delta) % matches.length);
            return;
          }
          // Tab aceita e segue para o campo seguinte; Enter aceita e fica. Com texto escrito
          // aceita-se logo (é o gesto de autocompletar); um valor já exato não é substituído.
          const canAccept = value.trim() !== "" ? !options.includes(value) : navigated;
          if ((event.key === "Tab" || event.key === "Enter") && canAccept) {
            if (event.key === "Enter") event.preventDefault();
            accept(matches[active]);
          }
        }}
        className="px-3 py-2 border border-border rounded bg-background text-foreground"
        style={{ borderRadius: "var(--radius-button)" }}
      />
      {isOpen && matches.length > 0 && (
        <div
          className="absolute left-0 top-full mt-1 w-full bg-card border border-border shadow-lg z-50 overflow-y-auto"
          style={{ maxHeight: "12rem", borderRadius: "var(--radius-button)" }}
          // mousedown cancelado para o input não perder o foco (blur fecharia o painel antes
          // do click acertar — inclui a barra de scroll).
          onMouseDown={(event) => event.preventDefault()}
        >
          {matches.map((option, index) => (
            <button
              key={option}
              type="button"
              className={`w-full px-3 py-2 text-left transition-colors ${
                index === active ? "bg-accent" : "hover:bg-accent"
              }`}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => accept(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Badge âmbar para a linha cujo nome foi lido do documento mas não casa com nenhuma obra. */
function NoMatchBadge() {
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs rounded font-medium whitespace-nowrap"
      style={{ color: "#b45309", backgroundColor: "rgba(245,158,11,0.16)" }}
    >
      Sem correspondência no sistema
    </span>
  );
}

/** Badge discreto com a origem da atribuição da linha ("porque é que esta linha ficou assim?"). */
function AssignmentSourceBadge({ code }: { code?: string | null }) {
  if (!code) return null;
  const meta = ASSIGNMENT_SOURCE_META[code];
  if (!meta) return null;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 text-xs rounded font-medium whitespace-nowrap"
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      {meta.label}
    </span>
  );
}

interface FormState {
  companyId: string;
  workId: string;
  expenseCategoryId: string;
  expenseSubcategoryId: string;
  // Sub-tipo de texto livre — só nas despesas de categoria sem obra.
  subType: string;
  supplierName: string;
  supplierTaxNumber: string;
  matchedSupplierId: string;
  documentNumber: string;
  documentDate: string;
  description: string;
  baseAmount: string;
  totalAmount: string;
  // Montante liquidado: lido do comprovativo, corrigível aqui.
  paidAmount: string;
  // Quando o fornecedor é texto livre: inserir também na tabela de fornecedores.
  createSupplier: boolean;
  // "Criar obra nova" no cabeçalho (despesa de obra única): a obra é criada ao guardar.
  createWorkMode: boolean;
  newWorkName: string;
  newWorkClientId: string;
  // Repartição por várias obras (Auto partilhado): quando ligada, a despesa não tem obra no cabeçalho.
  splitByWorks: boolean;
  allocations: AllocationRow[];
}

const EMPTY_FORM: FormState = {
  companyId: "",
  workId: "",
  expenseCategoryId: "",
  expenseSubcategoryId: "",
  subType: "",
  supplierName: "",
  supplierTaxNumber: "",
  matchedSupplierId: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  baseAmount: "",
  totalAmount: "",
  paidAmount: "",
  createSupplier: false,
  createWorkMode: false,
  newWorkName: "",
  newWorkClientId: "",
  splitByWorks: false,
  allocations: [],
};

const formatCurrency = (value?: number | null): string => {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
};

/**
 * Abaixo disto, a leitura assume que pode ter-se enganado. A extração devolve um grau de confiança
 * de 0 a 1 e ele nunca saía do servidor — passa a marcar as faturas que vale a pena conferir, que
 * é o que substitui a alternativa de não gravar nome nenhum quando há dúvida: mais vale um nome
 * assinalado do que uma despesa órfã que ninguém reconhece.
 */
const CONFIANCA_BAIXA = 0.7;

/**
 * A fase do ciclo documental, por extenso e com uma tinta a acompanhar o percurso. Derivada no
 * servidor — aqui só se apresenta.
 *
 * O texto usa o token `--foreground`, que vira com o tema, e não uma cor por fase: as cores
 * escritas à mão da primeira versão davam ~2:1 em modo escuro — ilegíveis — e é o mesmo erro já
 * corrigido no aviso de confiança. A tinta de fundo é reforço decorativo; quem distingue as fases
 * é o texto, que cada uma tem o seu.
 */
const CICLO: Record<string, { rotulo: string; fundo: string }> = {
  prevista: { rotulo: "Prevista", fundo: "rgba(245,196,66,0.22)" },
  "com-auto": { rotulo: "Com auto", fundo: "rgba(155,110,190,0.20)" },
  "com-fatura": { rotulo: "Com fatura", fundo: "rgba(66,165,201,0.20)" },
  paga: { rotulo: "Paga", fundo: "rgba(34,197,94,0.20)" },
};

function CicloPill({ fase }: { fase: string }) {
  const meta = CICLO[fase];
  // aria-label e não title: o nome acessível de um <span> com texto prefere o texto ("—") ao
  // title, e o leitor de ecrã ficava sem explicação nenhuma.
  if (!meta) return <span aria-label="Fase desconhecida">—</span>;
  return (
    <span
      className="text-xs whitespace-nowrap"
      style={{
        padding: "2px 8px",
        borderRadius: "var(--radius-button)",
        color: "var(--foreground)",
        backgroundColor: meta.fundo,
        fontWeight: "var(--font-weight-medium)",
      }}
    >
      {meta.rotulo}
    </span>
  );
}

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

/** Obras de uma linha sugerida para fusão, por extenso (escolhida, sugerida ou estrutura). */
const forecastSuggestionWorks = (works?: ForecastSuggestionWork[] | null): string => {
  if (!works || works.length === 0) return "por atribuir";
  return works
    .map((work) =>
      work.workId
        ? (work.workName ?? "—")
        : work.isStructural
          ? "estrutura (sem obra)"
          : work.suggestedWorkName
            ? `«${work.suggestedWorkName}» (por atribuir)`
            : "por atribuir",
    )
    .filter((label, index, labels) => labels.indexOf(label) === index)
    .join(", ");
};

const toDateInput = (value?: string | null): string => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const parseNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Com vírgula decimal, os pontos são separadores de milhares ("1.234,56"); sem vírgula, um
  // ponto continua a valer como decimal ("1.5") — remover pontos às cegas transformava-o em 15.
  const normalized = trimmed.includes(",")
    ? trimmed.replace(/\./g, "").replace(",", ".")
    : trimmed;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) return "Sessão expirada. Autentique-se novamente.";
    if (error.status === 403) return "Sem permissões para gerir faturas.";
    if (error.message) {
      // O corpo de um erro do servidor costuma ser um ProblemDetails JSON — extrai a
      // mensagem legível (detail/title) em vez de mostrar o JSON cru ao utilizador.
      try {
        const parsed = JSON.parse(error.message);
        if (parsed && typeof parsed === "object") {
          return parsed.detail || parsed.title || error.message;
        }
      } catch {
        // Não é JSON — usa o texto tal como veio.
      }
      return error.message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return "Ocorreu um erro. Tente novamente.";
};

/**
 * Reparte a categoria gravada na fatura pelos dois dropdowns. O modelo guarda só uma categoria:
 * quando é uma subcategoria, a raiz é o seu pai; quando é uma raiz, não há sub-categoria.
 */
const splitCategory = (
  expenseCategoryId: number | null | undefined,
  categories: ExpenseCategoryOption[],
): { expenseCategoryId: string; expenseSubcategoryId: string } => {
  const selected = expenseCategoryId
    ? categories.find((category) => category.id === expenseCategoryId)
    : undefined;

  if (!selected) {
    return { expenseCategoryId: "", expenseSubcategoryId: "" };
  }

  return selected.parentId
    ? {
        expenseCategoryId: String(selected.parentId),
        expenseSubcategoryId: String(selected.id),
      }
    : { expenseCategoryId: String(selected.id), expenseSubcategoryId: "" };
};

/**
 * Constrói o estado do formulário a partir de uma fatura (edição ou revisão de documento extraído).
 * A fatura guarda uma única categoria; se for uma subcategoria, é preciso `categories` para
 * descobrir a raiz e repor os dois dropdowns (Categoria + Sub-Categoria).
 */
const formFromInvoice = (
  invoice: Invoice,
  categories: ExpenseCategoryOption[],
): FormState => ({
  companyId: invoice.companyId ? String(invoice.companyId) : "",
  workId: invoice.workId ? String(invoice.workId) : "",
  ...splitCategory(invoice.expenseCategoryId, categories),
  subType: invoice.subType ?? "",
  supplierName: invoice.supplierName ?? "",
  supplierTaxNumber: invoice.supplierTaxNumber ?? "",
  matchedSupplierId: invoice.matchedSupplierId
    ? String(invoice.matchedSupplierId)
    : "",
  documentNumber: invoice.documentNumber ?? "",
  documentDate: toDateInput(invoice.documentDate),
  description: invoice.description ?? "",
  baseAmount: invoice.baseAmount != null ? String(invoice.baseAmount) : "",
  totalAmount: invoice.totalAmount != null ? String(invoice.totalAmount) : "",
  paidAmount: invoice.paidAmount != null ? String(invoice.paidAmount) : "",
  createSupplier: false,
  createWorkMode: false,
  newWorkName: "",
  newWorkClientId: "",
  splitByWorks: invoice.isSplit,
  allocations: invoice.allocations.map((allocation) => ({
    id: allocation.id,
    workId: allocation.workId ? String(allocation.workId) : "",
    suggestedWorkName: allocation.suggestedWorkName ?? "",
    amount: allocation.amount != null ? String(allocation.amount) : "",
    description: allocation.description ?? "",
    isStructural: allocation.isStructural ?? false,
    assignmentSourceCode: allocation.assignmentSourceCode ?? null,
    // Linha sem correspondência mas com nome lido do documento: arranca em modo "criar obra nova"
    // — é a ação mais provável, e o nome já vai pré-preenchido.
    createMode: !allocation.workId && !(allocation.isStructural ?? false) && !!allocation.suggestedWorkName,
    newWorkName: allocation.suggestedWorkName ?? "",
    newWorkClientId: "",
    createBusy: false,
    createError: null,
    feedbackTypeCode: allocation.feedbackTypeCode ?? null,
    feedbackErrorDescription: allocation.feedbackErrorDescription ?? null,
    feedbackCorrectedWorkId: allocation.feedbackCorrectedWorkId ?? null,
    feedbackAt: allocation.feedbackAt ?? null,
  })),
});

interface AllocationSummaryRowProps {
  allocation: InvoiceAllocation;
  works: WorkOption[];
  onFeedbackSaved: (feedback: AllocationFeedbackResult) => void;
  onFeedbackError: (message: string) => void;
}

/** Linha de leitura da repartição (obra ou "⚠ Por atribuir" + valor + 👍/👎), partilhada pela
 * linha expandida da tabela e pelo modal do documento. */
function AllocationSummaryRow({
  allocation,
  works,
  onFeedbackSaved,
  onFeedbackError,
}: AllocationSummaryRowProps) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="flex items-center gap-2 flex-wrap">
        {allocation.workId ? (
          <span className="text-foreground">{allocation.workName ?? "—"}</span>
        ) : allocation.isStructural ? (
          <span className="text-secondary-text">Estrutura (sem obra)</span>
        ) : (
          <span style={{ color: "var(--destructive)" }}>
            {allocation.suggestedWorkName
              ? allocation.assignmentSourceCode === "memory-similar"
                ? `⚠ Sugestão: ${allocation.suggestedWorkName} — por confirmar`
                : `⚠ «${allocation.suggestedWorkName}» — por atribuir`
              : "⚠ Por atribuir"}
          </span>
        )}
        <AssignmentSourceBadge code={allocation.assignmentSourceCode} />
        {allocation.description ? (
          <span className="text-secondary-text"> — {allocation.description}</span>
        ) : null}
      </span>
      <span className="flex items-center gap-2">
        <span style={{ fontWeight: "var(--font-weight-medium)" }}>
          {formatCurrency(allocation.amount)}
        </span>
        <AllocationFeedbackControl
          allocationId={allocation.id}
          feedbackTypeCode={allocation.feedbackTypeCode}
          feedbackErrorDescription={allocation.feedbackErrorDescription}
          feedbackCorrectedWorkId={allocation.feedbackCorrectedWorkId}
          feedbackAt={allocation.feedbackAt}
          works={works}
          onSaved={onFeedbackSaved}
          onError={onFeedbackError}
        />
      </span>
    </div>
  );
}

/** Determina se um ficheiro deve ser pré-visualizado como PDF (senão, como imagem). */
const isPdfFile = (file: File): boolean =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name);

/** Folhas de cálculo (autos em xls/xlsx) não têm pré-visualização no browser. */
const isSpreadsheetName = (name?: string | null): boolean =>
  /\.xlsx?$/i.test(name ?? "");

/** Aceites nos documentos de despesa — inclui folhas de cálculo (autos). */
const EXPENSE_DOCUMENT_ACCEPT =
  "application/pdf,image/png,image/jpeg,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,.xlsx,.xls";

/** O botão de um ficheiro anexado: Ver, quando o browser o consegue mostrar (o Descarregar fica
 * dentro do viewer); Descarregar, quando não (folhas de cálculo). */
function ViewOrDownloadButton({
  name,
  onView,
  onDownload,
}: {
  /** Só quando há vários ficheiros lado a lado: distingue os botões para leitores de ecrã. */
  name?: string;
  onView?: () => void;
  onDownload: () => void;
}) {
  const label = onView ? "Ver" : "Descarregar";
  return (
    <button
      type="button"
      aria-label={name ? `${label} ${name}` : undefined}
      className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
      style={{ color: "var(--primary)" }}
      onClick={onView ?? onDownload}
    >
      {onView ? (
        <Eye20Regular className="w-4 h-4" />
      ) : (
        <ArrowDownload20Regular className="w-4 h-4" />
      )}
      {label}
    </button>
  );
}

interface UploadBoxProps {
  title: string;
  icon: ReactNode;
  hasFile: boolean;
  fileName?: string | null;
  subtitle?: string | null;
  isUploading: boolean;
  /** Tipos aceites; por omissão PDF/imagens (o comprovativo não aceita folhas de cálculo). */
  accept?: string;
  onDownload: () => void;
  /** Quando definido, o botão passa a Ver: abre o documento na app (o Descarregar está lá dentro). */
  onView?: () => void;
  onSelectFile: (file: File) => void;
  /** Quando definido, mostra o botão de remover ao lado do de descarregar. */
  onRemove?: () => void;
  /** Nota mostrada na caixa vazia — explica porque é que está vazia e o que se espera aqui. */
  emptyHint?: string | null;
  /** Ação extra na linha de baixo (ex.: dizer que o documento afinal é um auto). */
  action?: { label: string; icon: ReactNode; onClick: () => void };
  /** Caixa que mostra mas não recebe ficheiros — a nota de vazio explica porquê. */
  readOnly?: boolean;
  /** Texto da caixa cheia. Por omissão "substituir". */
  replaceLabel?: string;
}

/** Caixa de upload (clique ou drag-and-drop) para anexar um ficheiro a uma fatura. */
function UploadBox({
  title,
  icon,
  hasFile,
  fileName,
  subtitle,
  isUploading,
  accept = "application/pdf,image/png,image/jpeg",
  onDownload,
  onView,
  onSelectFile,
  onRemove,
  emptyHint,
  action,
  readOnly = false,
  replaceLabel = "Clique ou arraste para substituir",
}: UploadBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const pickFile = (files: FileList | null) => {
    if (readOnly) return;
    const file = files?.[0];
    if (file) onSelectFile(file);
  };

  // As três caixas vivem lado a lado e a moldura de todas tem a MESMA altura, fixa — cada uma leva
  // a sua quantidade de texto e uma delas leva ainda a linha dos botões, e sem altura fixa a do
  // meio encolhia para lhes dar espaço.
  //
  // As alturas vão em estilo inline e não em classes Tailwind, e não é preferência: o `index.css`
  // deste projeto é uma folha COMPILADA e commitada, não gerada no build. Só tem os utilitários que
  // existiam no dia em que foi gerada — traz 17 alturas e 4 `min-h`, e mais nenhuma. Uma classe de
  // altura nova (`h-[240px]`, e até `h-40`) não tem regra e é ignorada em silêncio, sem erro nenhum.
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        {icon}
        <span>{title}</span>
      </div>
      <div
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? undefined : 0}
        onClick={() => {
          if (!readOnly) inputRef.current?.click();
        }}
        onKeyDown={(event) => {
          if (readOnly) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          pickFile(event.dataTransfer.files);
        }}
        className={`flex flex-col items-center justify-center gap-3 px-4 py-8 overflow-hidden border-2 border-dashed text-center transition-colors ${
          readOnly
            ? "border-border"
            : isDragging
              ? "border-primary bg-accent/30 cursor-pointer"
              : "border-border hover:bg-accent/30 cursor-pointer"
        }`}
        style={{ borderRadius: "var(--radius)", height: 240 }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = "";
          }}
        />
        {isUploading ? (
          <span className="text-sm text-secondary-text">A carregar...</span>
        ) : hasFile ? (
          <>
            {isSpreadsheetName(fileName) ? (
              <DocumentTable20Regular
                className="w-6 h-6"
                style={{ color: "var(--primary)" }}
              />
            ) : (
              <DocumentPdf20Regular
                className="w-6 h-6"
                style={{ color: "var(--primary)" }}
              />
            )}
            <span className="text-sm text-foreground break-all">
              {fileName ?? "Ficheiro anexado"}
            </span>
            {subtitle && (
              <span className="text-xs text-secondary-text">{subtitle}</span>
            )}
            <span className="text-xs text-secondary-text">
              {replaceLabel}
            </span>
          </>
        ) : (
          <>
            {!readOnly && (
              <>
                <ArrowUpload20Regular className="w-6 h-6 text-secondary-text" />
                <span className="text-sm text-secondary-text">
                  Arraste um ficheiro ou clique para escolher
                </span>
                <span className="text-xs text-secondary-text">
                  PDF, PNG ou JPG
                </span>
              </>
            )}
            {emptyHint && (
              <span className="text-xs text-secondary-text max-w-xs">
                {emptyHint}
              </span>
            )}
          </>
        )}
      </div>
      {/* Existe em todas as caixas, com ou sem botões, e com altura fixa — é o que mantém as
          bases alinhadas sem a moldura ter de encolher para dar espaço aos botões. */}
      <div className="flex items-center gap-1" style={{ height: 38 }}>
        {hasFile && !isUploading && (
          <>
            <ViewOrDownloadButton onView={onView} onDownload={onDownload} />
            {action && (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 border border-border bg-background text-foreground hover:bg-accent/30"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={action.onClick}
              >
                {action.icon}
                {action.label}
              </button>
            )}
            {onRemove && (
              <button
                className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
                style={{ color: "var(--destructive)" }}
                onClick={onRemove}
              >
                <Delete20Regular className="w-4 h-4" />
                Remover
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface FaturaFileEntry {
  key: string;
  name: string;
  onDownload: () => void;
  /** Quando definido, o botão passa a Ver (ver UploadBoxProps.onView). */
  onView?: () => void;
  onRemove: () => void;
}

interface FaturaBoxProps {
  files: FaturaFileEntry[];
  isUploading: boolean;
  accept?: string;
  emptyHint?: string | null;
  onSelectFile: (file: File) => void;
}

/** Caixa da Fatura — a única que acumula: 0 ficheiros mostra a área de upload, 1 mostra o ficheiro
 * (como as outras caixas), e a partir de 2 a MESMA moldura divide-se em partes iguais, uma por
 * fatura, cada uma com o seu Descarregar/Remover. Altura igual às caixas Auto/Comprovativo (240). */
function FaturaBox({
  files,
  isUploading,
  accept = "application/pdf,image/png,image/jpeg",
  emptyHint,
  onSelectFile,
}: FaturaBoxProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const openPicker = () => inputRef.current?.click();
  const pickFile = (list: FileList | null) => {
    const file = list?.[0];
    if (file) onSelectFile(file);
  };

  const count = files.length;
  const single = files[0];

  const fileIcon = (name: string) =>
    isSpreadsheetName(name) ? (
      <DocumentTable20Regular className="w-5 h-5 flex-shrink-0" style={{ color: "var(--primary)" }} />
    ) : (
      <DocumentPdf20Regular className="w-5 h-5 flex-shrink-0" style={{ color: "var(--primary)" }} />
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <DocumentPdf20Regular className="w-5 h-5" />
        <span>Fatura</span>
      </div>
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          pickFile(event.dataTransfer.files);
        }}
        className={`flex flex-col overflow-hidden border-2 border-dashed transition-colors ${
          isDragging ? "border-primary bg-accent/30" : "border-border"
        }`}
        style={{ borderRadius: "var(--radius)", height: 240 }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(event) => {
            pickFile(event.target.files);
            event.target.value = "";
          }}
        />
        {isUploading ? (
          <div
            className="flex-1 flex items-center justify-center"
            aria-live="polite"
          >
            <span className="text-sm text-secondary-text">A carregar...</span>
          </div>
        ) : count === 0 ? (
          <div
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openPicker();
              }
            }}
            className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-center cursor-pointer hover:bg-accent/30"
          >
            <ArrowUpload20Regular className="w-6 h-6 text-secondary-text" />
            <span className="text-sm text-secondary-text">
              Arraste um ficheiro ou clique para escolher
            </span>
            <span className="text-xs text-secondary-text">PDF, PNG ou JPG</span>
            {emptyHint && (
              <span className="text-xs text-secondary-text max-w-xs">{emptyHint}</span>
            )}
          </div>
        ) : count === 1 ? (
          <div
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                openPicker();
              }
            }}
            className="flex-1 flex flex-col items-center justify-center gap-3 px-4 text-center cursor-pointer hover:bg-accent/30"
          >
            {isSpreadsheetName(single.name) ? (
              <DocumentTable20Regular className="w-6 h-6" style={{ color: "var(--primary)" }} />
            ) : (
              <DocumentPdf20Regular className="w-6 h-6" style={{ color: "var(--primary)" }} />
            )}
            <span className="text-sm text-foreground break-all">{single.name}</span>
            <span className="text-xs text-secondary-text">
              Clique ou arraste para adicionar outra fatura
            </span>
          </div>
        ) : (
          <div className="flex-1 flex flex-col" role="list">
            {files.map((file, index) => (
              <div
                key={file.key}
                role="listitem"
                className="flex-1 flex flex-col items-center justify-center gap-2 px-4 text-center overflow-hidden"
                style={{
                  borderTop: index > 0 ? "2px dashed var(--border)" : undefined,
                }}
              >
                {fileIcon(file.name)}
                <span className="text-sm text-foreground break-all">
                  {file.name}
                </span>
                <div className="flex items-center gap-1">
                  <ViewOrDownloadButton
                    name={file.name}
                    onView={file.onView}
                    onDownload={file.onDownload}
                  />
                  <button
                    type="button"
                    aria-label={`Remover ${file.name}`}
                    className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
                    style={{ color: "var(--destructive)" }}
                    onClick={file.onRemove}
                  >
                    <Delete20Regular className="w-4 h-4" />
                    Remover
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1" style={{ height: 38 }}>
        {!isUploading && count === 1 && (
          <>
            <ViewOrDownloadButton onView={single.onView} onDownload={single.onDownload} />
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
              style={{ color: "var(--primary)", whiteSpace: "nowrap" }}
              onClick={openPicker}
            >
              <Add20Regular className="w-4 h-4" />
              Adicionar fatura
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
              style={{ color: "var(--destructive)" }}
              onClick={single.onRemove}
            >
              <Delete20Regular className="w-4 h-4" />
              Remover
            </button>
          </>
        )}
        {!isUploading && count >= 2 && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded hover:bg-accent/30"
            style={{ color: "var(--primary)", whiteSpace: "nowrap" }}
            onClick={openPicker}
          >
            <Add20Regular className="w-4 h-4" />
            Adicionar fatura
          </button>
        )}
      </div>
    </div>
  );
}

/** Campo de detalhe (label discreto em cima, valor em destaque) da modal de documentos. */
function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span
        className="text-xs uppercase text-secondary-text"
        style={{ letterSpacing: "0.06em" }}
      >
        {label}
      </span>
      <span className="text-sm font-medium text-foreground break-words">
        {value}
      </span>
    </div>
  );
}

export function FaturasView() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Depois do primeiro load a tabela fica sempre montada (o loader passa a esmaecimento) — os
  // filtros vivem no thead, e desmontá-la a cada reload matava o foco/popovers a meio da escrita.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [categories, setCategories] = useState<ExpenseCategoryOption[]>([]);
  const [works, setWorks] = useState<WorkOption[]>([]);
  // Clientes só alimentam os seletores "Cliente (dono da obra)" — vêm da camada de lookups
  // partilhada (projeção mínima + cache de sessão) em vez do getClients() completo.
  const { options: clientLookupOptions, loading: clientsLoading } =
    useLookupOptions("clients");
  const [companies, setCompanies] = useState<Company[]>([]);
  // "" = todas as empresas (vista combinada).
  const [companyFilter, setCompanyFilter] = useState<string>("");

  // A empresa que o formulário assume quando a fatura não traz nenhuma. Concreta e não um
  // sentinela "por omissão": quem lança tem de ver de que empresa se trata.
  const defaultCompanyId = useMemo(() => {
    const fallback =
      companies.find((company) => company.isDefault) ?? companies[0];
    return fallback ? String(fallback.id) : "";
  }, [companies]);

  // Faturas antigas (anteriores à separação) não têm empresa: o formulário mostra a por omissão
  // em vez de um campo vazio que se gravaria sem se perceber onde ia parar.
  const withDefaultCompany = useCallback(
    (state: FormState): FormState =>
      state.companyId ? state : { ...state, companyId: defaultCompanyId },
    [defaultCompanyId],
  );

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [subcontractors, setSubcontractors] = useState<Subcontractor[]>([]);
  const [statuses, setStatuses] = useState<ApprovalStatusOption[]>([]);

  const [search, setSearch] = useState("");
  const [structural, setStructural] = useState<StructuralFilter>("all");
  // Filtro "Fora do Resumo": liga-se para listar só as linhas excluídas do Resumo do ano.
  const [onlyExcludedFromSummary, setOnlyExcludedFromSummary] = useState(false);
  // "Por validar" é o número do menu: este filtro mostra exatamente essas linhas.
  const [onlyPendingValidation, setOnlyPendingValidation] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subTypeFilter, setSubTypeFilter] = useState("");
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Filtros de coluna (estilo Excel). Vazio = coluna sem filtro. Todos vão à query — um filtro
  // só do lado do cliente dessincronizava o total, que é calculado no servidor.
  const [workFilter, setWorkFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");
  const [createdFromFilter, setCreatedFromFilter] = useState("");
  const [createdToFilter, setCreatedToFilter] = useState("");
  // A lista é, por omissão, um mapa contabilístico ordenado pela data do documento. "entrada"
  // responde à outra pergunta, a de quem acabou de encaminhar uma fatura: o que chegou agora.
  const [sort, setSort] = useState<"documento" | "entrada">("documento");
  // Clicar na coluna já activa inverte o sentido. Sem isto, o segundo clique não fazia
  // absolutamente nada e a grelha parecia avariada.
  const [sortAscending, setSortAscending] = useState(false);

  const toggleSort = (coluna: "documento" | "entrada") => {
    if (coluna === sort) {
      setSortAscending((anterior) => !anterior);
      return;
    }
    setSort(coluna);
    setSortAscending(false);
  };
  const [documentNumberFilter, setDocumentNumberFilter] = useState("");
  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [netFilter, setNetFilter] = useState("");
  const [grossFilter, setGrossFilter] = useState("");
  const [documentsFilter, setDocumentsFilter] = useState("");

  // Opções dos dropdowns de coluna: só o que existe mesmo nas faturas de despesa. As tabelas de
  // Obras/Fornecedores não servem — listam entidades sem faturas e omitem as faturas cujo emitente
  // não foi casado com um fornecedor, que são a maioria.
  const [filterOptions, setFilterOptions] = useState<InvoiceFilterOptions>({
    entities: [],
    subTypes: [],
    works: [],
  });

  // Totais do conjunto filtrado inteiro, vindos do servidor. Não somar `invoices` no cliente:
  // a listagem traz no máximo uma página e a soma ficaria a menos assim que o filtro
  // apanhasse mais faturas do que isso.
  const [totals, setTotals] = useState<InvoiceTotals>({
    baseTotal: 0,
    vatTotal: 0,
    grandTotal: 0,
    count: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [docInvoice, setDocInvoice] = useState<Invoice | null>(null);
  // O auto vive numa só caixa: quando ainda é o documento de origem está no slot principal
  // (documentIsAuto); quando a fatura chega desce para um EntityDocument (secção "auto"). Esta
  // lista é essa segunda casa — carregada ao abrir a modal, para a caixa Auto mostrar o ficheiro.
  const [autoDocs, setAutoDocs] = useState<EntityDocumentDto[]>([]);
  // Faturas adicionais da linha (secção "invoice"): a 1.ª fatura é o documento principal, estas são
  // as restantes — uma linha pode ter mais do que uma fatura.
  const [invoiceDocs, setInvoiceDocs] = useState<EntityDocumentDto[]>([]);
  // Linha a destacar na grelha (ex.: a fatura original de um duplicado detetado) — o efeito faz
  // scroll até ela e o realce apaga-se sozinho passado uns segundos. O `nonce` incrementa a cada
  // clique: sem ele, clicar outra vez com o realce ainda ativo dava o mesmo estado e o React
  // ignorava (o botão "não fazia nada").
  const [highlight, setHighlight] = useState<{
    id: number;
    nonce: number;
  } | null>(null);

  useEffect(() => {
    if (highlight == null) return;
    const row = document.getElementById(`invoice-row-${highlight.id}`);
    if (!row) return; // ainda não está na lista (ex.: filtros a limpar) — tenta no próximo load
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setHighlight(null), 5000);
    return () => clearTimeout(timer);
  }, [highlight, invoices]);

  // Leva o utilizador à fatura: limpa filtros/pesquisa que a possam esconder e destaca a linha.
  // Se ela nem sequer estiver nas linhas carregadas e não houver nada para limpar, diz — antes
  // desistia em silêncio.
  const revealInvoice = (invoiceId: number) => {
    if (!invoices.some((invoice) => invoice.id === invoiceId)) {
      if (!hasColumnFilters && !search) {
        setActionError(
          "A fatura já existente não está nas 200 linhas mais recentes — procure-a com os filtros (ex.: pelo fornecedor ou pela data).",
        );
        return;
      }
      clearColumnFilters();
      setSearch("");
    }
    setHighlight((previous) => ({
      id: invoiceId,
      nonce: (previous?.nonce ?? 0) + 1,
    }));
  };

  // Posição já mostrada de cada aviso de duplicado (1 = a primeira; 0/ausente = ainda nenhuma).
  // O mesmo documento chega a ter dezenas de cópias: sem isto o botão levava sempre à mesma e as
  // restantes só apareciam apagando-a e voltando a subir o ficheiro.
  const [duplicateCursors, setDuplicateCursors] = useState<Record<number, number>>({});

  const showNextDuplicate = (documentId: number, invoiceIds: number[]) => {
    if (invoiceIds.length === 0) return;
    const next = ((duplicateCursors[documentId] ?? 0) % invoiceIds.length) + 1;
    setDuplicateCursors((previous) => ({ ...previous, [documentId]: next }));
    revealInvoice(invoiceIds[next - 1]);
  };

  // Apagar uma das cópias tem de descer o contador do aviso: senão continuava a prometer faturas
  // que já não existem e o botão saltava para uma linha desaparecida. Devolve os avisos que ficaram
  // sem nenhuma fatura existente — esses já não têm motivo para estar a travar nada.
  const forgetDuplicateInvoice = (invoiceId: number): number[] => {
    const cursorUpdates: Record<number, number> = {};
    const emptied: number[] = [];
    let changed = false;

    const nextJobs = jobs.map((job) => {
      const ids = job.duplicateOfInvoiceIds ?? [];
      const index = ids.indexOf(invoiceId);
      if (index < 0) return job;

      changed = true;
      const remaining = ids.filter((id) => id !== invoiceId);
      if (remaining.length === 0) emptied.push(job.documentId);
      // O cursor é a posição já mostrada: se a apagada vinha antes, tudo desce uma casa; se era a
      // que estava a ser mostrada, recua para o clique seguinte mostrar a que ficou no lugar dela.
      const cursor = duplicateCursors[job.documentId] ?? 0;
      const moved =
        cursor > index + 1 ? cursor - 1 : cursor === index + 1 ? index : cursor;
      cursorUpdates[job.documentId] = Math.min(moved, remaining.length);
      return { ...job, duplicateOfInvoiceIds: remaining };
    });

    if (!changed) return [];
    setJobs(nextJobs);
    setDuplicateCursors((previous) => ({ ...previous, ...cursorUpdates }));
    return emptied;
  };

  // Fechar no fundo só quando o gesto COMEÇA e ACABA no fundo — arrastar a seleção de texto de
  // um input (ex.: o valor s/IVA) para fora do painel dispara o click no fundo (alvo = ancestral
  // comum) e fechava o modal a meio do registo, perdendo tudo (feedback do cliente).
  const backdropPressRef = useRef(false);
  const pressBackdrop = (event: React.MouseEvent) => {
    backdropPressRef.current = event.target === event.currentTarget;
  };
  const releasedOnBackdrop = (event: React.MouseEvent) =>
    backdropPressRef.current && event.target === event.currentTarget;
  // Fatura repartida cuja linha de detalhe (obras + valores) está expandida na tabela.
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<number | null>(null);

  // Comprovativo que cobre várias faturas: a proposta fica em memória até o utilizador confirmar,
  // e só aí é que alguma coisa é liquidada.
  const [proofProposal, setProofProposal] = useState<PaymentProofProposal | null>(null);
  const [proofBusy, setProofBusy] = useState(false);
  const [proofSearch, setProofSearch] = useState("");
  const [proofError, setProofError] = useState<string | null>(null);
  const [proofChosen, setProofChosen] = useState<number[]>([]);

  // A procura filtra o que se vê, nunca o que já está escolhido: escrever no filtro não pode
  // desfazer uma selecção feita antes, nem esconder que ela existe — o total lá em baixo conta
  // sempre as escolhidas todas.
  const proofPayableVisible = useMemo(() => {
    const term = proofSearch.trim().toLowerCase();
    const payable = proofProposal?.payable ?? [];
    if (term === "") return payable;

    return payable.filter(
      (invoice) =>
        (invoice.supplierName ?? "").toLowerCase().includes(term) ||
        (invoice.documentNumber ?? "").toLowerCase().includes(term),
    );
  }, [proofProposal, proofSearch]);
  const proofInputRef = useRef<HTMLInputElement>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const [isValidatingSelection, setIsValidatingSelection] = useState(false);
  const [isTogglingSelectionExclusion, setIsTogglingSelectionExclusion] = useState(false);
  const [isSchedulingPayment, setIsSchedulingPayment] = useState(false);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Fecha o menu de exportação ao clicar fora dele (mesmo padrão do menu de estado do Orçamento).
  useEffect(() => {
    if (!isExportMenuOpen) return;
    const handler = (event: MouseEvent) => {
      if (!exportMenuRef.current?.contains(event.target as Node)) {
        setIsExportMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isExportMenuOpen]);
  const [uploadingKind, setUploadingKind] = useState<
    "document" | "proof" | "auto" | null
  >(null);
  const [docError, setDocError] = useState<string | null>(null);
  // Documento aberto no viewer, por cima da lista ou da janela de documentos.
  const [viewerDocument, setViewerDocument] = useState<ViewerDocument | null>(null);
  // Aviso (não erro): o comprovativo foi guardado mas o valor não foi lido — diz porquê, para o
  // utilizador corrigir à mão em vez de ficar sem explicação.
  const [docNotice, setDocNotice] = useState<string | null>(null);
  // Fundir/desfazer desmontam o próprio botão clicado: o foco tem de aterrar algures (o título
  // da modal) e o botão precisa de estado ocupado para não disparar duas vezes.
  const docModalHeadingRef = useRef<HTMLHeadingElement>(null);
  const [mergingForecastId, setMergingForecastId] = useState<number | null>(null);
  const [unmergingForecast, setUnmergingForecast] = useState(false);

  // Upload de documentos de despesa para extração AI (Azure Document Intelligence).
  const docInputRef = useRef<HTMLInputElement>(null);
  const [jobs, setJobs] = useState<InvoiceExtraction[]>([]);
  // Ids sintéticos (negativos) para uploads que falharam ainda antes de arrancar (sem documentId real).
  const failedJobKeyRef = useRef(-1);
  const [actionError, setActionError] = useState<string | null>(null);
  // O que correu BEM tem canal próprio: liquidar faturas a vermelho, no mesmo sítio onde aparecem
  // os erros, lê-se como se a operação tivesse falhado quando foi o contrário.
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  // O número do menu (despesas por validar), para o ecrã dizer o que fazer com ele. Vem do
  // ShellLayout, que já o pede ao servidor a cada recarga desta lista.
  const [pendingValidationCount, setPendingValidationCount] = useState<number | null>(null);
  useEffect(() => {
    const onSummary = (event: Event) => {
      const summary = (event as CustomEvent<AttentionSummary>).detail;
      setPendingValidationCount(summary?.pendingValidation ?? null);
    };
    window.addEventListener("aja2:attention-summary", onSummary);
    return () => window.removeEventListener("aja2:attention-summary", onSummary);
  }, []);
  // Atalho para as Tarefas, só enquanto o aviso no ecrã é o do pagamento combinado.
  const [tasksLinkNotice, setTasksLinkNotice] = useState<string | null>(null);

  // Propaga um veredicto 👍/👎 acabado de gravar a todas as cópias da linha em memória
  // (lista, modal do documento e formulário de edição), sem reload.
  const applyAllocationFeedback = useCallback((feedback: AllocationFeedbackResult) => {
    const patch = (allocation: InvoiceAllocation): InvoiceAllocation =>
      allocation.id === feedback.allocationId
        ? {
            ...allocation,
            feedbackTypeCode: feedback.feedbackTypeCode,
            feedbackErrorDescription: feedback.errorDescription ?? null,
            feedbackCorrectedWorkId: feedback.correctedWorkId ?? null,
            feedbackAt: feedback.feedbackAt,
          }
        : allocation;

    setInvoices((prev) =>
      prev.map((invoice) =>
        invoice.id === feedback.workFinancialEntryId
          ? { ...invoice, allocations: invoice.allocations.map(patch) }
          : invoice,
      ),
    );
    setDocInvoice((prev) =>
      prev && prev.id === feedback.workFinancialEntryId
        ? { ...prev, allocations: prev.allocations.map(patch) }
        : prev,
    );
    setForm((prev) => ({
      ...prev,
      allocations: prev.allocations.map((row) =>
        row.id === feedback.allocationId
          ? {
              ...row,
              feedbackTypeCode: feedback.feedbackTypeCode,
              feedbackErrorDescription: feedback.errorDescription ?? null,
              feedbackCorrectedWorkId: feedback.correctedWorkId ?? null,
              feedbackAt: feedback.feedbackAt,
            }
          : row,
      ),
    }));
  }, []);

  // Revisão do documento extraído: preview (object URL local) por documentId,
  // fila de faturas a rever e a sessão de revisão ativa (preview + formulário).
  const previewsRef = useRef<Map<number, { url: string; isPdf: boolean }>>(
    new Map(),
  );
  const createdUrlsRef = useRef<Set<string>>(new Set());
  const [reviewQueue, setReviewQueue] = useState<
    { invoice: Invoice; url: string; isPdf: boolean }[]
  >([]);
  const [review, setReview] = useState<{
    invoice: Invoice;
    url: string;
    isPdf: boolean;
  } | null>(null);

  // Os filtros ativos, construídos num único sítio — a listagem e a exportação partilham este
  // objeto para o ficheiro exportado não poder divergir da grelha.
  const activeFilters = useMemo<InvoiceFilters>(
    () => ({
      companyId: companyFilter ? Number(companyFilter) : null,
      search: search || null,
      subType: subTypeFilter || null,
      lifecycle: lifecycleFilter || null,
      structural: structural === "all" ? null : structural === "structural",
      expenseCategoryId: categoryFilter ? Number(categoryFilter) : null,
      approvalStatusCode: statusFilter || null,
      workId: workFilter ? Number(workFilter) : null,
      entity: supplierFilter || null,
      from: dateFromFilter || null,
      to: dateToFilter || null,
      createdFrom: toLocalDayIso(createdFromFilter),
      createdTo: toLocalDayIso(createdToFilter),
      sort,
      sortAscending,
      documentNumber: documentNumberFilter || null,
      description: descriptionFilter || null,
      net: toAmount(netFilter),
      gross: toAmount(grossFilter),
      documents: documentsFilter || null,
      excludedFromSummary: onlyExcludedFromSummary ? true : null,
      pendingOnly: onlyPendingValidation ? true : null,
    }),
    [
      companyFilter,
      search,
      structural,
      categoryFilter,
      subTypeFilter,
      lifecycleFilter,
      statusFilter,
      workFilter,
      supplierFilter,
      dateFromFilter,
      dateToFilter,
      createdFromFilter,
      createdToFilter,
      sort,
      sortAscending,
      documentNumberFilter,
      descriptionFilter,
      netFilter,
      grossFilter,
      documentsFilter,
      onlyExcludedFromSummary,
      onlyPendingValidation,
    ],
  );

  // Nº de sequência dos loads: com vários inputs debounced, dois pedidos podem sobrepor-se e o
  // mais antigo chegar por último — sem a guarda, repintava grelha e rodapé com filtros já velhos.
  const loadSequenceRef = useRef(0);

  const loadInvoices = useCallback(async () => {
    const sequence = ++loadSequenceRef.current;
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getInvoices({ ...activeFilters, pageSize: 200 });
      if (loadSequenceRef.current !== sequence) return;
      setInvoices(result.items);
      setTotalCount(result.totalCount);
      setTotals(result.totals);
    } catch (error) {
      if (loadSequenceRef.current !== sequence) return;
      setInvoices([]);
      setTotals({ baseTotal: 0, vatTotal: 0, grandTotal: 0, count: 0 });
      setLoadError(parseErrorMessage(error));
    } finally {
      if (loadSequenceRef.current === sequence) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
      // Cada recarga segue-se a algo que pode ter mudado o que está por validar (validar, apagar,
      // juntar a fatura a um auto, editar…): o número do menu acompanha logo, sem esperar pela
      // mudança de ecrã — mesmo que a recarga falhe depois de a alteração ter entrado.
      window.dispatchEvent(new Event("aja2:attention"));
    }
  }, [activeFilters]);

  const hasColumnFilters = Boolean(
    companyFilter ||
      categoryFilter ||
      subTypeFilter ||
      lifecycleFilter ||
      workFilter ||
      supplierFilter ||
      statusFilter ||
      dateFromFilter ||
      dateToFilter ||
      createdFromFilter ||
      createdToFilter ||
      documentNumberFilter ||
      descriptionFilter ||
      netFilter ||
      grossFilter ||
      documentsFilter ||
      onlyExcludedFromSummary ||
      onlyPendingValidation,
  );

  const clearColumnFilters = useCallback(() => {
    setCompanyFilter("");
    setCategoryFilter("");
    setSubTypeFilter("");
    setLifecycleFilter("");
    setWorkFilter("");
    setSupplierFilter("");
    setStatusFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setCreatedFromFilter("");
    setCreatedToFilter("");
    setDocumentNumberFilter("");
    setDescriptionFilter("");
    setNetFilter("");
    setGrossFilter("");
    setDocumentsFilter("");
    setOnlyExcludedFromSummary(false);
    setOnlyPendingValidation(false);
  }, []);

  // As opções dos filtros de coluna são "o que existe mesmo nas faturas" — e por isso ficam
  // velhas assim que uma gravação, extração ou fusão introduz um fornecedor, obra ou sub-tipo
  // novo. Antes carregavam-se uma vez por mount e um fornecedor acabado de criar não aparecia no
  // filtro sem refrescar a página; agora cada mutação que pode mudar a lista volta a pedi-las.
  // Best-effort de propósito: falhar isto não pode partir a gravação que acabou de acontecer.
  const refreshFilterOptions = useCallback(() => {
    getInvoiceFilterOptions()
      .then(setFilterOptions)
      .catch(() => {
        /* fica a lista anterior; o refresh da página resolve */
      });
  }, []);

  useEffect(() => {
    let active = true;
    // `works`/`suppliers` alimentam o formulário de nova fatura, onde faz sentido oferecer tudo o
    // que existe. Os filtros de coluna usam `filterOptions`, que só traz o que tem faturas.
    Promise.all([
      getExpenseCategories(),
      getWorks(),
      getSuppliers(),
      getApprovalStatuses(),
      getInvoiceFilterOptions(),
      getCompanies(),
    ])
      .then(([cats, wks, sups, sts, options, comps]) => {
        if (!active) return;
        setCategories(cats);
        setWorks(wks);
        setSuppliers(sups);
        setStatuses(sts);
        setFilterOptions(options);
        setCompanies(comps);
      })
      .catch(() => {
        /* reference data is best-effort; the list still loads */
      });

    // Sub-empreiteiros à parte do Promise.all: são só mais opções no seletor de fornecedor —
    // uma falha aqui não pode inutilizar as referências essenciais do formulário.
    getSubcontractors()
      .then((subs) => {
        if (!active) return;
        setSubcontractors(subs);
      })
      .catch(() => {
        /* opcional: o seletor fica só com fornecedores e texto livre */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const handle = setTimeout(loadInvoices, 250);
    return () => clearTimeout(handle);
  }, [loadInvoices]);

  // Polling do estado de extração dos documentos submetidos (à imagem do fluxo dentro da Obra).
  // Quando algum fica "Ready", recarrega a lista para mostrar a nova fatura (por validar).
  useEffect(() => {
    const activeIds = jobs
      .filter(
        (job) =>
          job.processingStatus === "Pending" ||
          job.processingStatus === "Processing",
      )
      .map((job) => job.documentId);
    if (activeIds.length === 0) return;

    let cancelled = false;
    const handle = setInterval(async () => {
      const results = await Promise.allSettled(
        activeIds.map((id) => getInvoiceDocumentStatus(id)),
      );
      if (cancelled) return;

      const updates = new Map<number, InvoiceExtraction>();
      const readyDocIds: number[] = [];
      const newReviews: { invoice: Invoice; url: string; isPdf: boolean }[] = [];
      results.forEach((result, index) => {
        const documentId = activeIds[index];
        if (result.status !== "fulfilled") {
          // Poll falhou (ex.: erro do servidor) — marca como falhado para parar o polling.
          const existing = jobs.find((job) => job.documentId === documentId);
          updates.set(documentId, {
            documentId,
            fileName: existing?.fileName ?? "Documento",
            processingStatus: "Failed",
            processingError: parseErrorMessage(result.reason),
          });
          return;
        }
        const value = result.value;
        updates.set(documentId, value);
        if (value.processingStatus === "Ready" && value.invoice) {
          readyDocIds.push(value.documentId);
          const preview = previewsRef.current.get(value.documentId);
          newReviews.push({
            invoice: value.invoice,
            url: preview?.url ?? "",
            isPdf: preview?.isPdf ?? true,
          });
          previewsRef.current.delete(value.documentId);
        }
      });

      // Aplica os estados novos; retira do banner os que ficaram prontos (passam à revisão).
      setJobs((prev) =>
        prev
          .map((job) => updates.get(job.documentId) ?? job)
          .filter((job) => !readyDocIds.includes(job.documentId)),
      );
      if (newReviews.length > 0) {
        setReviewQueue((prev) => [...prev, ...newReviews]);
        refreshFilterOptions();
        loadInvoices();
      }
    }, 3500);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [jobs, loadInvoices, refreshFilterOptions]);

  // Abre a próxima revisão da fila quando não há nenhuma dialog de revisão ativa.
  useEffect(() => {
    if (review || reviewQueue.length === 0) return;
    const [next, ...rest] = reviewQueue;
    setReviewQueue(rest);
    setReview(next);
    setEditingId(next.invoice.id);
    setForm(withDefaultCompany(formFromInvoice(next.invoice, categories)));
    setFormError(null);
  }, [review, reviewQueue, categories, withDefaultCompany]);

  // Liberta os object URLs de preview quando o componente desmonta.
  useEffect(() => {
    const urls = createdUrlsRef.current;
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
      urls.clear();
    };
  }, []);

  // As categorias vêm da API numa lista plana com parentId; o 1º nível é o das raízes.
  // Categoria selecionada no formulário. Categorias "de obra" (Obra, Obra de Manutenção) têm
  // isStructural === false: nessas a obra é obrigatória; nas de estrutura não há obra.
  const selectedCategory = form.expenseCategoryId
    ? categories.find((category) => String(category.id) === form.expenseCategoryId)
    : undefined;
  const isWorkCategory = selectedCategory?.isStructural === false;

  // Sub-categorias da raiz escolhida. Nas categorias de obra a "subcategoria" do Excel é a
  // própria obra, por isso não têm filhos e o dropdown não chega a aparecer.
  const subcategories = form.expenseCategoryId
    ? categories.filter(
        (category) => String(category.parentId ?? "") === form.expenseCategoryId,
      )
    : [];

  // Repartição por obras: soma das fatias vs total s/IVA do cabeçalho (o resto tem de fechar a 0 para imputar).
  const allocatedSum = form.allocations.reduce(
    (sum, row) => sum + (parseNumber(row.amount) ?? 0),
    0,
  );
  const netTotal = parseNumber(form.baseAmount);
  const remainder =
    netTotal != null ? Math.round((netTotal - allocatedSum) * 100) / 100 : null;

  const updateAllocation = (index: number, patch: Partial<AllocationRow>) => {
    setForm((prev) => ({
      ...prev,
      allocations: prev.allocations.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addAllocationRow = () => {
    setForm((prev) => ({
      ...prev,
      allocations: [
        ...prev.allocations,
        {
          workId: "",
          suggestedWorkName: "",
          amount: "",
          description: "",
          isStructural: false,
          createMode: false,
          newWorkName: "",
          newWorkClientId: "",
          createBusy: false,
          createError: null,
        },
      ],
    }));
  };

  const removeAllocationRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      allocations: prev.allocations.filter((_, i) => i !== index),
    }));
  };

  // Reparte o total s/IVA por igual pelas linhas existentes; o resto do arredondamento vai para a 1ª.
  const distributeEvenly = () => {
    const count = form.allocations.length;
    if (count === 0 || netTotal == null) return;
    const base = Math.floor((netTotal / count) * 100) / 100;
    const remainderCents = Math.round((netTotal - base * count) * 100) / 100;
    setForm((prev) => ({
      ...prev,
      allocations: prev.allocations.map((row, i) => ({
        ...row,
        amount: String(i === 0 ? Math.round((base + remainderCents) * 100) / 100 : base),
      })),
    }));
  };

  // As empresas mudam nas Definições, que vivem noutro ecrã: relê-las ao abrir o formulário
  // evita o F5 a que uma empresa acabada de criar obrigava.
  const refreshCompanies = () => {
    getCompanies()
      .then(setCompanies)
      .catch(() => {
        // Lista desatualizada é melhor do que um erro: o formulário continua utilizável.
      });
  };

  const openCreate = () => {
    refreshCompanies();
    setEditingId(null);
    // Herda a empresa que está a ser vista: quem lança a partir da folha da Semear não quer
    // que a fatura caia na Jaime Aleixo por omissão.
    setForm({ ...EMPTY_FORM, companyId: companyFilter || defaultCompanyId });
    setFormError(null);
    setIsModalOpen(true);
  };

  // A ficha aberta é de uma fatura por validar: o rodapé oferece "Guardar e validar".
  // Na revisão de um documento acabado de subir a fatura pode ainda não estar na lista filtrada.
  const editingInvoicePending =
    editingId != null &&
    [...(review ? [review.invoice] : []), ...invoices].some(
      (invoice) => invoice.id === editingId && invoice.validationStatus === "PendingValidation",
    );

  const openEdit = (invoice: Invoice) => {
    refreshCompanies();
    setEditingId(invoice.id);
    setForm(withDefaultCompany(formFromInvoice(invoice, categories)));
    setFormError(null);
    setIsModalOpen(true);
  };

  // validateAfter: "Guardar e validar" — quem acabou de conferir a ficha (valores, obra,
  // fornecedor) confirma-a no mesmo gesto, em vez de ter de descobrir a seleção da lista.
  const handleSave = async (validateAfter = false) => {
    if (form.splitByWorks && form.allocations.length === 0) {
      setFormError("Adicione pelo menos uma obra à repartição.");
      return;
    }
    if (!form.matchedSupplierId && !form.supplierName.trim()) {
      setFormError("Indique o fornecedor (nome livre ou selecione um da lista).");
      return;
    }
    const baseAmount = parseNumber(form.baseAmount);
    const totalAmount = parseNumber(form.totalAmount);
    if (baseAmount === null || totalAmount === null) {
      setFormError("Preencha o valor s/IVA e o valor c/IVA.");
      return;
    }
    // Linhas com o toggle "Criar obra nova" ligado: valida os dados antes de arrancar — as obras
    // criam-se automaticamente ao guardar (mesma regra do fornecedor de texto livre, abaixo).
    if (form.splitByWorks) {
      for (let rowIndex = 0; rowIndex < form.allocations.length; rowIndex++) {
        const row = form.allocations[rowIndex];
        if (!row.createMode || row.workId || row.isStructural) continue;
        const workName = (row.newWorkName || row.suggestedWorkName).trim();
        if (!workName) {
          setFormError(`Indique o nome da obra a criar (linha ${rowIndex + 1} da repartição).`);
          return;
        }
        if (!row.newWorkClientId) {
          setFormError(`Escolha o cliente (dono da obra) para «${workName}».`);
          return;
        }
      }
    }
    // Cabeçalho com "Criar obra nova" ligado (despesa de obra única): mesma validação das linhas.
    const headerCreateWork =
      form.createWorkMode && isWorkCategory && !form.splitByWorks && !form.workId;
    if (headerCreateWork) {
      if (!form.newWorkName.trim()) {
        setFormError("Indique o nome da obra a criar.");
        return;
      }
      if (!form.newWorkClientId) {
        setFormError(`Escolha o cliente (dono da obra) para «${form.newWorkName.trim()}».`);
        return;
      }
    }
    // IVA calculado automaticamente (c/IVA − s/IVA).
    const vatAmount = Math.round((totalAmount - baseAmount) * 100) / 100;

    setIsSaving(true);
    setFormError(null);
    try {
      // Fornecedor a ligar. Se pediram para inserir o fornecedor de texto livre,
      // liga a um existente com o mesmo NIF (evita duplicar) ou cria-o.
      let matchedSupplierId = form.matchedSupplierId
        ? Number(form.matchedSupplierId)
        : null;
      if (!matchedSupplierId && form.createSupplier && form.supplierName.trim()) {
        const taxId = form.supplierTaxNumber.trim();
        const normalized = taxId.replace(/\s/g, "");
        const existing = normalized
          ? suppliers.find(
              (supplier) =>
                (supplier.taxId ?? "").replace(/\s/g, "") === normalized,
            )
          : undefined;
        if (existing) {
          matchedSupplierId = existing.id;
        } else {
          const created = await createSupplier({
            name: form.supplierName.trim(),
            taxId: taxId || null,
          });
          matchedSupplierId = created.id;
          setSuppliers((prev) => [...prev, created]);
        }
      }

      // O modelo guarda uma categoria só: a sub-categoria, quando escolhida, é a mais específica
      // e já traz a raiz pelo seu parentId.
      const effectiveCategoryId =
        form.expenseSubcategoryId || form.expenseCategoryId;

      // Obras novas (toggle ligado): criam-se agora, uma a uma. Cada criação bem-sucedida fica
      // logo refletida no formulário — se uma falhar a meio, o novo Guardar não duplica as já criadas.
      let effectiveAllocations = form.allocations;
      if (form.splitByWorks) {
        const updated = [...form.allocations];
        for (let rowIndex = 0; rowIndex < updated.length; rowIndex++) {
          const row = updated[rowIndex];
          if (!row.createMode || row.workId || row.isStructural) continue;
          const createdWork = await createWork({
            name: (row.newWorkName || row.suggestedWorkName).trim(),
            clientId: Number(row.newWorkClientId),
          });
          setWorks((prev) => [
            ...prev,
            {
              id: createdWork.id,
              code: createdWork.code,
              name: createdWork.name,
              statusCode: createdWork.statusCode,
            },
          ]);
          updated[rowIndex] = {
            ...row,
            workId: String(createdWork.id),
            createMode: false,
            createError: null,
          };
          const snapshot = updated[rowIndex];
          setForm((prev) => ({
            ...prev,
            allocations: prev.allocations.map((item, itemIndex) =>
              itemIndex === rowIndex ? snapshot : item,
            ),
          }));
        }
        effectiveAllocations = updated;
      }

      // Obra nova no cabeçalho: cria-se agora; a criação bem-sucedida fica logo refletida no
      // formulário — se o guardar falhar a seguir, o novo Guardar não a duplica.
      let effectiveWorkId = form.workId;
      if (headerCreateWork) {
        const createdWork = await createWork({
          name: form.newWorkName.trim(),
          clientId: Number(form.newWorkClientId),
        });
        setWorks((prev) => [
          ...prev,
          {
            id: createdWork.id,
            code: createdWork.code,
            name: createdWork.name,
            statusCode: createdWork.statusCode,
          },
        ]);
        effectiveWorkId = String(createdWork.id);
        setForm((prev) => ({
          ...prev,
          workId: String(createdWork.id),
          createWorkMode: false,
        }));
      }

      // Repartição: quando ligada, envia as fatias (o cabeçalho fica sem obra); quando desligada,
      // envia [] para limpar qualquer repartição anterior (conjunto autoritário).
      const allocations: InvoiceAllocationInput[] = form.splitByWorks
        ? effectiveAllocations.map((row) => ({
            id: row.id ?? null,
            workId: row.isStructural ? null : row.workId ? Number(row.workId) : null,
            suggestedWorkName: row.suggestedWorkName.trim() || null,
            amount: parseNumber(row.amount) ?? 0,
            description: row.description.trim() || null,
            isStructural: row.isStructural,
          }))
        : [];

      const payload: InvoicePayload = {
        companyId: form.companyId ? Number(form.companyId) : null,
        workId: form.splitByWorks ? null : effectiveWorkId ? Number(effectiveWorkId) : null,
        expenseCategoryId: effectiveCategoryId
          ? Number(effectiveCategoryId)
          : null,
        subType: isWorkCategory ? null : form.subType.trim() || null,
        supplierName: matchedSupplierId ? null : form.supplierName.trim() || null,
        supplierTaxNumber: matchedSupplierId
          ? null
          : form.supplierTaxNumber.trim() || null,
        matchedSupplierId,
        documentNumber: form.documentNumber.trim() || null,
        documentDate: form.documentDate
          ? new Date(form.documentDate).toISOString()
          : null,
        description: form.description.trim() || null,
        baseAmount,
        vatAmount,
        totalAmount,
        paidAmount: parseNumber(form.paidAmount),
        allocations,
      };

      const saved =
        editingId != null
          ? await updateInvoice(editingId, payload)
          : await createInvoice(payload);

      if (validateAfter && saved.validationStatus === "PendingValidation") {
        await validateInvoices([saved.id]);
        setActionNotice("Fatura guardada e validada.");
      }

      // A folha segue a fatura: guardá-la numa empresa que a vista não mostra fazia-a
      // desaparecer, e obrigava a ir ao seletor lá fora só para ver o que se acabou de gravar.
      if (
        companyFilter &&
        saved.companyId != null &&
        String(saved.companyId) !== companyFilter
      ) {
        setCompanyFilter(String(saved.companyId));
      }
      setIsModalOpen(false);
      closeReview();
      refreshFilterOptions();
      await loadInvoices();
    } catch (error) {
      setFormError(parseErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  // Devolve true se apagou mesmo — quem chama a partir de uma janela (ex.: revisão) só a deve
  // fechar nesse caso, não quando o utilizador desiste na confirmação.
  const handleProofUpload = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file || proofBusy) return;
    setProofBusy(true);
    setProofError(null);
    try {
      const proposal = await matchPaymentProof(file);
      setProofProposal(proposal);
      // Limpa a procura da vez anterior: um filtro esquecido esconderia faturas no comprovativo
      // seguinte, e a lista pareceria vazia sem razão à vista.
      setProofSearch("");
      // Pré-seleciona o conjunto mais simples; com vários, o utilizador escolhe.
      setProofChosen(
        proposal.matches.length === 1
          ? proposal.matches[0].invoices.map((invoice) => invoice.invoiceId)
          : [],
      );
    } catch (error) {
      setProofError(parseErrorMessage(error));
    } finally {
      setProofBusy(false);
    }
  };

  const toggleProofInvoice = (invoiceId: number) =>
    setProofChosen((previous) =>
      previous.includes(invoiceId)
        ? previous.filter((id) => id !== invoiceId)
        : [...previous, invoiceId],
    );

  const confirmProof = async () => {
    if (!proofProposal || proofChosen.length === 0 || proofBusy) return;
    setProofBusy(true);
    setProofError(null);
    try {
      const result = await applyPaymentProof({
        blobName: proofProposal.blobName,
        fileName: proofProposal.fileName,
        amount: proofProposal.proofAmount ?? 0,
        invoiceIds: proofChosen,
      });
      setProofProposal(null);
      setProofChosen([]);
      setActionError(null);
      await loadInvoices();
      const parts = [
        result.settledCount === 1
          ? "1 fatura liquidada"
          : `${result.settledCount} faturas liquidadas`,
      ];
      if (result.partiallySettledCount > 0) {
        parts.push(
          result.partiallySettledCount === 1
            ? "1 parcialmente"
            : `${result.partiallySettledCount} parcialmente`,
        );
      }
      if (result.unapplied > 0) {
        parts.push(`sobraram ${formatCurrency(result.unapplied)} por aplicar`);
      }
      setActionNotice(parts.join(" · "));
    } catch (error) {
      setProofError(parseErrorMessage(error));
    } finally {
      setProofBusy(false);
    }
  };

  const handleDelete = async (invoice: Invoice): Promise<boolean> => {
    if (
      !window.confirm(
        `Apagar a fatura de ${invoice.supplierName ?? invoice.matchedSupplierName ?? "fornecedor"}? Esta ação remove também o PDF e o comprovativo.`,
      )
    ) {
      return false;
    }
    try {
      await deleteInvoice(invoice.id);
      // Se a fatura apagada estava aberta na modal de documentos, fecha-a.
      setDocInvoice((current) => (current?.id === invoice.id ? null : current));
      const emptied = forgetDuplicateInvoice(invoice.id);
      refreshFilterOptions();
      await loadInvoices();
      // Apagada a última cópia, o documento que tinha ficado de fora deixou de ser duplicado de
      // alguma coisa — importa-se, em vez de ficar um aviso a apontar para faturas que já não
      // existem. Só o primeiro: importar todos os avisos de uma vez recriava a duplicação que se
      // acabou de limpar (os seguintes voltam a ser duplicados assim que este entrar).
      if (emptied.length > 0) {
        await importDuplicateAnyway(emptied[0]);
      }
      return true;
    } catch (error) {
      // actionError e não loadError: uma ação falhada não pode apagar a tabela do ecrã — era o que
      // acontecia, ficava só a mensagem vermelha e a lista desaparecia.
      setActionError(parseErrorMessage(error));
      return false;
    }
  };

  // Validar a seleção inteira num gesto: as pendentes ficam validadas, as outras não mudam.
  const handleValidateSelected = async () => {
    const ids = selection.ids;
    if (ids.length === 0 || isValidatingSelection) return;
    setIsValidatingSelection(true);
    setActionError(null);
    try {
      const result = await validateInvoices(ids);
      await loadInvoices();
      setActionNotice(`${result.validated} fatura(s) validada(s)`);
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsValidatingSelection(false);
    }
  };

  // Validar uma linha: o mesmo gesto da seleção, sem ter de a descobrir — era a dúvida do cliente
  // ("dados certos, obra escolhida, continua Por validar — o que falta?").
  const [validatingId, setValidatingId] = useState<number | null>(null);
  const handleValidateOne = async (invoice: Invoice) => {
    if (validatingId !== null) return;
    setValidatingId(invoice.id);
    setActionError(null);
    try {
      await validateInvoices([invoice.id]);
      await loadInvoices();
      setActionNotice(
        invoice.documentNumber ? `Fatura ${invoice.documentNumber} validada.` : "Fatura validada.",
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setValidatingId(null);
    }
  };

  // Apagar a seleção inteira num gesto: a mesma limpeza do apagar individual, fatura a fatura no
  // servidor, e uma que falhe não trava as restantes.
  const handleDeleteSelected = async () => {
    const ids = selection.ids;
    if (ids.length === 0 || isDeletingSelection) return;
    if (
      !window.confirm(
        `Apagar ${ids.length} fatura(s) selecionada(s)? Esta ação remove também os PDFs e os comprovativos.`,
      )
    ) {
      return;
    }
    setIsDeletingSelection(true);
    setActionError(null);
    try {
      const result = await deleteInvoices(ids);
      setDocInvoice((current) =>
        current && ids.includes(current.id) ? null : current,
      );
      const emptied = ids.flatMap((id) => forgetDuplicateInvoice(id));
      refreshFilterOptions();
      await loadInvoices();
      if (emptied.length > 0) {
        await importDuplicateAnyway(emptied[0]);
      }
      const parts = [`${result.deleted} fatura(s) apagada(s)`];
      if (result.failed > 0) {
        parts.push(`${result.failed} falharam${result.error ? `: ${result.error}` : ""}`);
      }
      setActionNotice(parts.join(" · "));
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsDeletingSelection(false);
    }
  };

  const handleStatusChange = async (invoice: Invoice, statusCode: string) => {
    try {
      await setInvoiceApprovalStatus(invoice.id, statusCode);
      await loadInvoices();
    } catch (error) {
      setActionError(parseErrorMessage(error));
    }
  };

  // Imputa (ou anula) a fatura ao ledger de custos. Numa fatura repartida cria um custo por obra.
  const handleImpute = async (invoice: Invoice, impute: boolean) => {
    try {
      const updated = impute
        ? await imputeInvoiceCost(invoice.id)
        : await removeInvoiceCostImputation(invoice.id);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    }
  };

  const openDocuments = (invoice: Invoice) => {
    setDocInvoice(invoice);
    setDocError(null);
  };

  // Carrega os autos guardados como EntityDocument (secção "auto") da fatura — é a casa do auto
  // depois de a fatura chegar. Chamado ao abrir a modal e depois de subir a fatura.
  const refreshAutoDocs = useCallback(async (entryId: number) => {
    try {
      const docs = await getEntityDocuments("financial-entry", entryId, {
        section: "auto",
      });
      setAutoDocs(docs);
    } catch (error) {
      // Se a lista falhar, a caixa Auto cai para o auto de origem (slot principal) — não bloqueia,
      // mas deixa rasto: uma falha de rede não pode passar por "sem autos" sem aviso nenhum.
      console.error("Falha a carregar os autos da fatura", error);
      setAutoDocs([]);
    }
  }, []);

  // Faturas adicionais (secção "invoice") da linha aberta — para a caixa Fatura dividida.
  const refreshInvoiceDocs = useCallback(async (entryId: number) => {
    try {
      const docs = await getEntityDocuments("financial-entry", entryId, {
        section: "invoice",
      });
      setInvoiceDocs(docs);
    } catch (error) {
      console.error("Falha a carregar as faturas adicionais", error);
      setInvoiceDocs([]);
    }
  }, []);

  useEffect(() => {
    if (!docInvoice) {
      setAutoDocs([]);
      setInvoiceDocs([]);
      return;
    }
    void refreshAutoDocs(docInvoice.id);
    void refreshInvoiceDocs(docInvoice.id);
  }, [docInvoice, refreshAutoDocs, refreshInvoiceDocs]);

  // A partir da modal de documentos: fecha-a e abre a modal de edição da fatura.
  const editFromDocuments = () => {
    if (!docInvoice) return;
    const invoice = docInvoice;
    setDocInvoice(null);
    openEdit(invoice);
  };

  // A linha (ou a lista inteira) que tinha o foco é removida do DOM ao remover um comprovativo; sem
  // isto o foco caía para o <body>. Devolvemo-lo ao cabeçalho da lista enquanto sobrar pelo menos um
  // comprovativo; se foi o último (a lista desmonta), recua ao cabeçalho da modal.
  const proofListHeadingRef = useRef<HTMLDivElement>(null);

  const handleRemoveProof = async (proofId: number) => {
    if (!docInvoice) return;
    if (
      !window.confirm(
        "Remover este comprovativo? O montante que ele pagou é subtraído do valor pago da linha.",
      )
    ) {
      return;
    }
    setUploadingKind("proof");
    setDocError(null);
    try {
      const updated = await removeInvoicePaymentProof(proofId);
      setDocInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      requestAnimationFrame(() =>
        (proofListHeadingRef.current ?? docModalHeadingRef.current)?.focus(),
      );
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  const handleRemoveFatura = async () => {
    if (!docInvoice) return;
    if (!window.confirm("Remover a fatura desta linha?")) return;
    setUploadingKind("document");
    setDocError(null);
    try {
      const updated = await removeInvoiceDocument(docInvoice.id);
      setDocInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  // Referência da NC editável no modal; sincroniza quando se muda de linha.
  const [annulmentReference, setAnnulmentReference] = useState("");
  // Em curso: desarma os toggles para um duplo clique não disparar pedidos concorrentes
  // (o mesmo contrato do uploadingKind nas outras mutações deste modal).
  const [savingCreditNote, setSavingCreditNote] = useState(false);
  // Desativar o controlo que tem o foco atira-o para o <body> (o modal não tem trap) — no fim
  // devolve-se ao próprio controlo, como os outros handlers do modal devolvem ao cabeçalho.
  const creditNoteCheckboxRef = useRef<HTMLInputElement>(null);
  const annulCheckboxRef = useRef<HTMLInputElement>(null);
  const summaryExclusionCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setAnnulmentReference(docInvoice?.creditNoteReference ?? "");
  }, [docInvoice?.id, docInvoice?.creditNoteReference]);

  const handleToggleCreditNote = async (isCreditNote: boolean) => {
    if (!docInvoice) return;
    setDocError(null);
    setSavingCreditNote(true);
    try {
      const updated = await setInvoiceCreditNote(docInvoice.id, isCreditNote);
      setDocInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      // Marcar pode ter anulado OUTRA linha, desmarcar desanula as que a NC tinha anulado — nas
      // duas direções há linhas de fora a mudar; o mapa acima só atualiza a própria.
      void loadInvoices();
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setSavingCreditNote(false);
      requestAnimationFrame(() => creditNoteCheckboxRef.current?.focus());
    }
  };

  const handleSetAnnulment = async (isAnnulled: boolean) => {
    if (!docInvoice) return;
    setDocError(null);
    setSavingCreditNote(true);
    try {
      const updated = await setInvoiceAnnulment(
        docInvoice.id,
        isAnnulled,
        annulmentReference.trim() || null,
      );
      setDocInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setSavingCreditNote(false);
      requestAnimationFrame(() => annulCheckboxRef.current?.focus());
    }
  };

  // Excluir do Resumo é uma decisão leve e reversível: a linha continua viva em todo o lado,
  // só deixa de somar no Resumo do ano.
  const handleSetSummaryExclusion = async (excluded: boolean) => {
    if (!docInvoice) return;
    setDocError(null);
    setSavingCreditNote(true);
    try {
      const updated = await setInvoiceSummaryExclusion(docInvoice.id, excluded);
      setDocInvoice(updated);
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setSavingCreditNote(false);
      requestAnimationFrame(() => summaryExclusionCheckboxRef.current?.focus());
    }
  };

  const handleDocUpload = async (kind: "document" | "proof", file: File) => {
    if (!docInvoice) return;
    setUploadingKind(kind);
    setDocError(null);
    setDocNotice(null);
    try {
      const updated =
        kind === "proof"
          ? await uploadInvoicePaymentProof(docInvoice.id, file)
          : await uploadInvoiceDocument(docInvoice.id, file);
      setDocInvoice(updated);
      // Refletir na lista sem recarregar tudo (o endpoint devolve a fatura atualizada).
      setInvoices((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item)),
      );
      // Comprovativo guardado mas sem valor lido: mostra a razão (serviço, sem texto, formato).
      if (kind === "proof" && updated.paymentProofReadNote) {
        setDocNotice(updated.paymentProofReadNote);
      }
      // A fatura, ao ocupar o slot principal, pode ter empurrado o auto de origem para a lista de
      // autos (KeepAsAttachment, no backend) — recarrega-a para a caixa Auto mostrar esse ficheiro.
      if (kind === "document") {
        await refreshAutoDocs(updated.id);
      }
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  // O auto tem casa própria: um único EntityDocument (secção "auto"). Subir substitui o que lá
  // estava — a caixa mostra um ficheiro só, como a Fatura e o Comprovativo.
  const handleAutoUpload = async (file: File) => {
    if (!docInvoice) return;
    setUploadingKind("auto");
    setDocError(null);
    try {
      for (const existing of autoDocs) {
        await deleteEntityDocument(existing.id);
      }
      // Se o auto ainda ocupa o slot do documento principal (origem por email/AI, ainda sem
      // EntityDocument), é ELE que está a ser substituído: libertá-lo primeiro. Sem isto ficava um
      // ficheiro órfão (blob + linha) que a UI já não mostrava nem deixava apagar.
      let base = docInvoice;
      if (docInvoice.documentIsAuto) {
        base = await removeInvoiceDocument(docInvoice.id);
      }
      const rawTitle = file.name.replace(/\.[^.]+$/, "").trim();
      const created = await uploadEntityDocument({
        file,
        ownerType: "financial-entry",
        ownerId: docInvoice.id,
        section: "auto",
        title: (rawTitle || "Auto").slice(0, 200),
      });
      setAutoDocs([created]);
      setDocInvoice(base);
      setInvoices((prev) =>
        prev.map((item) => (item.id === base.id ? base : item)),
      );
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  const handleAutoRemove = async () => {
    if (!docInvoice || autoDocs.length === 0) return;
    if (!window.confirm("Remover o auto desta fatura?")) return;
    setUploadingKind("auto");
    setDocError(null);
    try {
      for (const existing of autoDocs) {
        await deleteEntityDocument(existing.id);
      }
      setAutoDocs([]);
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  // A Fatura acumula: a 1.ª vai para o documento principal (slot 1:1, com a lógica de origem-auto do
  // backend); a partir daí cada nova fatura entra como anexo (secção "invoice"). A caixa Fatura
  // mostra-as todas, dividida. Assim uma linha pode ter mais do que uma fatura.
  const handleFaturaUpload = async (file: File) => {
    if (!docInvoice) return;
    const hasPrimary = docInvoice.hasDocument && !docInvoice.documentIsAuto;
    if (!hasPrimary) {
      await handleDocUpload("document", file);
      return;
    }
    setUploadingKind("document");
    setDocError(null);
    try {
      const rawTitle = file.name.replace(/\.[^.]+$/, "").trim();
      const created = await uploadEntityDocument({
        file,
        ownerType: "financial-entry",
        ownerId: docInvoice.id,
        section: "invoice",
        title: (rawTitle || "Fatura").slice(0, 200),
      });
      // Prepend para acompanhar a ordenação do servidor (mais recente primeiro) — senão a fatura
      // acabada de juntar saltava de posição quando o modal recarregasse a lista.
      setInvoiceDocs((prev) => [created, ...prev]);
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUploadingKind(null);
    }
  };

  // Faturas adicionais com um DELETE em curso — evita que um duplo-clique dispare dois pedidos (o
  // segundo falharia "já apagada" e mostrava um erro por uma operação que afinal teve sucesso).
  const removingInvoiceIds = useRef<Set<number>>(new Set());

  const handleInvoiceExtraRemove = async (id: number) => {
    if (removingInvoiceIds.current.has(id)) return;
    if (!window.confirm("Remover esta fatura da linha?")) return;
    removingInvoiceIds.current.add(id);
    setDocError(null);
    try {
      await deleteEntityDocument(id);
      setInvoiceDocs((prev) => prev.filter((doc) => doc.id !== id));
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      removingInvoiceIds.current.delete(id);
    }
  };

  // Todas as faturas da linha, na mesma ordem em que a caixa Fatura as mostra: a principal
  // (documento 1:1) primeiro, depois as adicionais (secção "invoice"). Com 1 fica igual às outras
  // caixas; com 2+ a moldura divide-se, uma parte por fatura.
  const faturaFiles: FaturaFileEntry[] = [];
  if (docInvoice && docInvoice.hasDocument && !docInvoice.documentIsAuto) {
    faturaFiles.push({
      key: "primary",
      name: docInvoice.documentFileName ?? "Fatura",
      onDownload: () =>
        runDownload(downloadInvoiceDocument(docInvoice), setDocError),
      onView: isSpreadsheetName(docInvoice.documentFileName)
        ? undefined
        : () =>
            setViewerDocument({
              name: docInvoice.documentFileName ?? "Fatura",
              load: () => fetchInvoiceDocument(docInvoice),
            }),
      onRemove: handleRemoveFatura,
    });
  }
  if (docInvoice) {
    for (const doc of invoiceDocs) {
      faturaFiles.push({
        key: `extra-${doc.id}`,
        name: doc.fileName,
        onDownload: () =>
          window.open(getEntityDocumentFileUrl(doc.id), "_blank", "noopener"),
        onView: isSpreadsheetName(doc.fileName)
          ? undefined
          : () =>
              setViewerDocument({
                name: doc.fileName,
                load: () => fetchEntityDocumentFile(doc.id),
              }),
        onRemove: () => handleInvoiceExtraRemove(doc.id),
      });
    }
  }

  // Fusão sugerida: a fatura completa a previsão escolhida. O resultado é a linha da previsão
  // (a da fatura morre), por isso recarrega-se a lista em vez de trocar a linha no sítio.
  // Um auto que chegou depois da fatura junta-se à linha dela (o servidor decide pelo tipo dos
  // documentos); muda só o aviso.
  const mergeForecast = async (invoiceEntryId: number, forecastEntryId: number, joinsAuto = false) => {
    if (mergingForecastId !== null) return;
    setDocError(null);
    setMergingForecastId(forecastEntryId);
    try {
      const merged = await completeForecast(invoiceEntryId, forecastEntryId);
      setDocInvoice(merged);
      refreshFilterOptions();
      await loadInvoices();
      setActionNotice(
        joinsAuto
          ? "Auto junto à fatura — fica nos autos e anexos da linha dela."
          : "Fatura fundida com a despesa prevista — o valor previsto fica guardado na ficha.",
      );
      docModalHeadingRef.current?.focus();
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setMergingForecastId(null);
    }
  };

  const undoForecastMerge = async () => {
    if (!docInvoice || unmergingForecast) return;
    setDocError(null);
    setUnmergingForecast(true);
    try {
      const restored = await unmergeForecast(docInvoice.id);
      setDocInvoice(restored);
      refreshFilterOptions();
      await loadInvoices();
      setActionNotice("Fusão desfeita: a previsão voltou ao valor previsto e a fatura é outra vez uma linha própria.");
      docModalHeadingRef.current?.focus();
    } catch (error) {
      setDocError(parseErrorMessage(error));
    } finally {
      setUnmergingForecast(false);
    }
  };

  const dismissJob = (documentId: number) =>
    setJobs((prev) => prev.filter((job) => job.documentId !== documentId));

  // Duplicado que é mesmo para importar (ex.: duas faturas verdadeiramente iguais): força a
  // reclassificação para fatura e volta a pô-lo na fila — o polling existente retoma o cartão
  // e abre a revisão quando a fatura nascer.
  const importDuplicateAnyway = async (documentId: number) => {
    try {
      await reclassifyDocument(documentId);
      setJobs((prev) =>
        prev.map((job) =>
          job.documentId === documentId
            ? {
                ...job,
                processingStatus: "Pending",
                classification: null,
                classificationReason: null,
                duplicateOfInvoiceIds: [],
              }
            : job,
        ),
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    }
  };

  // Dispara um download tratando a rejeição (senão um 500/404 vira "uncaught runtime error").
  const runDownload = (
    promise: Promise<void>,
    onError: (message: string) => void,
  ) => {
    promise.catch((error) => onError(parseErrorMessage(error)));
  };

  // Fecha a dialog de revisão e liberta o object URL do preview. A próxima revisão
  // da fila (se existir) abre automaticamente pelo efeito respetivo.
  const closeReview = useCallback(() => {
    setReview((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
        createdUrlsRef.current.delete(current.url);
      }
      return null;
    });
  }, []);

  // ESC fecha os modais da página — quem abre documento atrás de documento sentia o custo de ir
  // à cruz de cada vez (feedback do cliente). Fecha só a janela de cima (revisão > detalhe do
  // documento > formulário), com o mesmo efeito do clique no fundo, que já descartava sem avisar.
  useEffect(() => {
    if (!review && !docInvoice && !isModalOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (review) closeReview();
      else if (docInvoice) setDocInvoice(null);
      else setIsModalOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [review, docInvoice, isModalOpen, closeReview]);

  // Sobe um ou mais documentos de despesa; cada um é processado de forma assíncrona
  // (OCR + AI). O estado é acompanhado por polling e a fatura aparece na lista quando pronta.
  const handleExtractUpload = async (files: FileList | null) => {
    const list = files ? Array.from(files) : [];
    for (const file of list) {
      try {
        const started = await extractInvoiceFromDocument(file);
        // Guarda o preview local do ficheiro para o mostrar na dialog de revisão.
        const previewUrl = URL.createObjectURL(file);
        createdUrlsRef.current.add(previewUrl);
        previewsRef.current.set(started.documentId, {
          url: previewUrl,
          isPdf: isPdfFile(file),
        });
        setJobs((prev) => [started, ...prev]);
      } catch (error) {
        setJobs((prev) => [
          {
            documentId: failedJobKeyRef.current--,
            fileName: file.name,
            processingStatus: "Failed",
            processingError: parseErrorMessage(error),
          },
          ...prev,
        ]);
      }
    }
  };

  const structuralBadge = (value?: boolean | null) => {
    if (value === true) {
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
          style={{
            backgroundColor: "var(--accent)",
            color: "var(--accent-foreground)",
          }}
        >
          <Building20Regular className="w-3.5 h-3.5" />
          Estrutura
        </span>
      );
    }
    return null;
  };

  /**
   * Etiqueta da categoria para leitura. A fatura guarda só a categoria mais específica, por isso
   * uma sub-categoria sozinha ("Restaurante") perdia o contexto — mostra-se "Estaleiro › Restaurante".
   */
  const categoryLabel = (invoice: Invoice): string => {
    if (!invoice.expenseCategoryName) return "—";
    const selected = invoice.expenseCategoryId
      ? categories.find((category) => category.id === invoice.expenseCategoryId)
      : undefined;
    const parent = selected?.parentId
      ? categories.find((category) => category.id === selected.parentId)
      : undefined;
    return parent
      ? `${parent.name} › ${invoice.expenseCategoryName}`
      : invoice.expenseCategoryName;
  };

  // Os autos são maioritariamente de sub-empreiteiros, que vivem numa tabela própria sem registo
  // em Fornecedores — sem isto, quem revê um auto não encontra a entidade na lista e escreve o
  // nome à mão. Quem existe nas duas tabelas (mesmo nome) fica só como fornecedor, que é a opção
  // que liga a fatura ao registo.
  const subcontractorChoices = useMemo(() => {
    const supplierNames = new Set(
      suppliers.map((supplier) => supplier.name.trim().toLocaleLowerCase("pt-PT")),
    );
    const seen = new Set<string>();
    return subcontractors
      .filter((subcontractor) => {
        const name = subcontractor.name?.trim();
        if (!name) return false;
        const key = name.toLocaleLowerCase("pt-PT");
        if (supplierNames.has(key) || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name, "pt-PT"));
  }, [suppliers, subcontractors]);

  // Um sub-empreiteiro escolhido entra como texto livre (a fatura só tem chave para Fornecedores),
  // por isso a seleção reconstrói-se a partir do nome — e um nome editado à mão que não conste da
  // lista volta, por definição, a "texto livre".
  const matchedSubcontractor =
    !form.matchedSupplierId && form.supplierName.trim()
      ? subcontractorChoices.find(
          (subcontractor) =>
            subcontractor.name.trim().toLocaleLowerCase("pt-PT") ===
            form.supplierName.trim().toLocaleLowerCase("pt-PT"),
        )
      : undefined;

  // Opções dos seletores com pesquisa do formulário, no formato do <SearchableSelect>.
  // Memoizadas: o índice de pesquisa reconstrói-se quando a lista muda, não a cada render.
  const clientSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      clientLookupOptions.map((option) => ({
        ...option,
        value: String(option.value),
      })),
    [clientLookupOptions],
  );

  const workSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      // Opcional por pedido do cliente: nem toda a despesa nasce já com obra — pode
      // associar-se depois, na edição.
      { value: "", label: "— Sem obra (associar mais tarde) —" },
      ...works.map((work) => ({
        value: String(work.id),
        label: work.name,
        keywords: work.code,
      })),
    ],
    [works],
  );

  const allocationWorkOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: STRUCTURAL_OPTION, label: "Estrutura (sem obra)" },
      ...works.map((work) => ({
        value: String(work.id),
        label:
          work.statusCode === "encerrada" ? `${work.name} (encerrada)` : work.name,
        keywords: work.code,
      })),
    ],
    [works],
  );

  // Fornecedores e sub-empreiteiros numa lista única (o <SearchableSelect> não tem grupos):
  // a `description` diz de que lado vem cada entrada; o NIF fica pesquisável via keywords.
  const supplierSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Fornecedor (texto livre)" },
      ...suppliers.map((supplier) => ({
        value: String(supplier.id),
        label: supplier.name,
        description: "Fornecedor",
        keywords: supplier.taxId ?? undefined,
      })),
      ...subcontractorChoices.map((subcontractor) => ({
        value: `sub:${subcontractor.id}`,
        label: subcontractor.name,
        description: "Sub-empreiteiro",
        keywords: subcontractor.taxId ?? undefined,
      })),
    ],
    [suppliers, subcontractorChoices],
  );

  // Filtros de coluna Obra/Fornecedor: mantêm o `filterOptions` (só o que tem faturas);
  // a opção de valor vazio é o "sem filtro" destas colunas.
  const workFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todas" },
      ...filterOptions.works.map((work) => ({
        value: String(work.id),
        label: work.code ? `${work.code} — ${work.name}` : work.name,
      })),
    ],
    [filterOptions.works],
  );

  // Ancorados em `categories` (estado) e não em `categoryRoots`/`subcategories`, que são
  // recalculados a cada render: com esses nas dependências o memo nunca acertava.
  const categorySelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      categories
        .filter((category) => !category.parentId)
        .map((category) => ({
          value: String(category.id),
          label: `${category.name}${category.isStructural === true ? " (estrutura)" : ""}`,
        })),
    [categories],
  );

  const subcategorySelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () =>
      categories
        .filter(
          (category) =>
            String(category.parentId ?? "") === form.expenseCategoryId,
        )
        .map((subcategory) => ({
          value: String(subcategory.id),
          label: subcategory.name,
        })),
    [categories, form.expenseCategoryId],
  );

  // Os filhos mantêm o travessão que os indenta, e ganham o nome da raiz em `keywords`:
  // procurar pela categoria-mãe passa a trazer também as suas sub-categorias.
  const categoryFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todas" },
      ...categories
        .filter((category) => !category.parentId)
        .flatMap((root) => [
          { value: String(root.id), label: root.name },
          ...categories
            .filter((category) => category.parentId === root.id)
            .map((child) => ({
              value: String(child.id),
              label: `— ${child.name}`,
              keywords: root.name,
            })),
        ]),
    ],
    [categories],
  );

  const supplierFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todos" },
      ...filterOptions.entities.map((name) => ({ value: name, label: name })),
    ],
    [filterOptions.entities],
  );

  const subTypeFilterSelectOptions = useMemo<SearchableSelectOption<string>[]>(
    () => [
      { value: "", label: "Todos" },
      { value: "sem-sub-tipo", label: "Sem sub-tipo" },
      ...filterOptions.subTypes.map((subType) => ({
        value: subType,
        label: subType,
      })),
    ],
    [filterOptions.subTypes],
  );

  // Campos do formulário de fatura, partilhados entre o modal (criar/editar) e a
  // dialog de revisão do documento extraído.
  const invoiceFormFields = (
    <>
      {companies.length > 1 && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-secondary-text">Empresa</span>
          <select
            value={form.companyId}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, companyId: event.target.value }))
            }
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {companies.map((company) => (
              <option key={company.id} value={String(company.id)}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <SearchableSelect<string>
        label="Categoria"
        labelClassName="text-sm text-secondary-text"
        labelStyle={{}}
        value={form.expenseCategoryId === "" ? null : form.expenseCategoryId}
        onChange={(next) => {
          const value = next ?? "";
          const nextCategory = value
            ? categories.find((category) => String(category.id) === value)
            : undefined;
          const nextIsWork = nextCategory?.isStructural === false;
          setForm((prev) => ({
            ...prev,
            expenseCategoryId: value,
            // A sub-categoria pertence à raiz anterior — deixa de fazer sentido.
            expenseSubcategoryId: "",
            // Categorias sem obra não têm obra associada — limpa a seleção.
            workId: nextIsWork ? prev.workId : "",
          }));
        }}
        options={categorySelectOptions}
        placeholder="— Selecionar —"
        searchPlaceholder="Procurar categoria…"
      />

      {subcategories.length > 0 && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-secondary-text">Sub-Categoria</span>
          <SearchableSelect<string>
            value={
              form.expenseSubcategoryId === "" ? null : form.expenseSubcategoryId
            }
            onChange={(next) =>
              setForm((prev) => ({
                ...prev,
                expenseSubcategoryId: next ?? "",
              }))
            }
            options={subcategorySelectOptions}
            ariaLabel="Sub-Categoria"
            placeholder="— Selecionar —"
            searchPlaceholder="Procurar sub-categoria…"
          />
        </label>
      )}

      {/* Despesa sem obra (categoria de estrutura): sub-tipo de texto livre com auto-sugestão
          dos já usados, pedido do cliente. */}
      {selectedCategory && !isWorkCategory && (
        <SubTypeSuggestField
          value={form.subType}
          options={filterOptions.subTypes}
          onChange={(subType) => setForm((prev) => ({ ...prev, subType }))}
        />
      )}

      {isWorkCategory && !form.splitByWorks && (
        <div className="flex flex-col gap-2">
          {form.createWorkMode && !form.workId ? (
            <>
              <div className="flex flex-wrap items-end gap-2">
                <label className="flex flex-col gap-1 flex-1" style={{ minWidth: "10rem" }}>
                  <span className="text-sm text-secondary-text">Nome da obra</span>
                  <input
                    type="text"
                    value={form.newWorkName}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, newWorkName: event.target.value }))
                    }
                    className="px-3 py-2 border border-border rounded bg-background text-foreground"
                    style={{ borderRadius: "var(--radius-button)" }}
                  />
                </label>
                <SearchableSelect<string>
                  className="flex-1"
                  style={{ minWidth: "10rem" }}
                  label="Cliente (dono da obra)"
                  required
                  labelClassName="text-sm text-secondary-text"
                  labelStyle={{}}
                  value={form.newWorkClientId === "" ? null : form.newWorkClientId}
                  onChange={(next) =>
                    setForm((prev) => ({ ...prev, newWorkClientId: next ?? "" }))
                  }
                  options={clientSelectOptions}
                  loading={clientsLoading}
                  placeholder="— Selecionar cliente —"
                  searchPlaceholder="Procurar cliente…"
                  panelPosition="fixed"
                />
              </div>
            </>
          ) : (
            <SearchableSelect<string>
              label="Obra"
              labelClassName="text-sm text-secondary-text"
              labelStyle={{}}
              value={form.workId}
              onChange={(next) =>
                setForm((prev) => ({ ...prev, workId: next ?? "" }))
              }
              options={workSelectOptions}
              allowClear={false}
              searchPlaceholder="Procurar obra…"
              panelPosition="fixed"
            />
          )}
          {/* Se a obra do documento ainda não existir, cria-se aqui mesmo — mesmo padrão das
              linhas da repartição. */}
          {!form.workId && (
            <ToggleSwitch
              checked={form.createWorkMode}
              onChange={(next) =>
                setForm((prev) => ({ ...prev, createWorkMode: next }))
              }
              label="Criar obra nova"
            />
          )}
        </div>
      )}

      <div className="md:col-span-2 flex flex-col gap-2">
        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={form.splitByWorks}
            onChange={(event) => {
              const checked = event.target.checked;
              setForm((prev) => ({
                ...prev,
                splitByWorks: checked,
                // Ao ligar, arranca com uma linha (pré-preenchida com a obra do cabeçalho, se houver).
                allocations:
                  checked && prev.allocations.length === 0
                    ? [
                        {
                          workId: prev.workId,
                          suggestedWorkName: "",
                          amount: prev.baseAmount,
                          description: prev.description,
                          isStructural: false,
                          createMode: false,
                          newWorkName: "",
                          newWorkClientId: "",
                          createBusy: false,
                          createError: null,
                        },
                      ]
                    : prev.allocations,
              }));
            }}
          />
          <span>Repartir esta despesa por várias obras (Auto partilhado)</span>
        </label>

        {form.splitByWorks && (
          <div
            className="flex flex-col gap-3 border border-border rounded p-3"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {form.allocations.map((row, index) => (
              <div
                key={row.id ?? `new-${index}`}
                className="flex flex-col gap-2 border border-border rounded p-3 bg-muted/30"
                style={{ borderRadius: "var(--radius-button)" }}
              >
                {/* Cabeçalho do cartão: a identidade da secção (nome tal como vem no documento) em
                    destaque e sem truncar, estado ao lado, ações à direita. O select fica em baixo,
                    limpo, só com a ação de escolher. */}
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="text-sm flex items-center gap-2 flex-wrap"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {row.suggestedWorkName ? `«${row.suggestedWorkName}»` : `Obra ${index + 1}`}
                    <AssignmentSourceBadge code={row.assignmentSourceCode} />
                    {!row.workId &&
                    !row.isStructural &&
                    row.suggestedWorkName &&
                    row.assignmentSourceCode !== "memory-similar" ? (
                      <NoMatchBadge />
                    ) : null}
                  </span>
                  <span className="flex items-center gap-2 flex-shrink-0">
                    {row.id ? (
                      <AllocationFeedbackControl
                        allocationId={row.id}
                        feedbackTypeCode={row.feedbackTypeCode}
                        feedbackErrorDescription={row.feedbackErrorDescription}
                        feedbackCorrectedWorkId={row.feedbackCorrectedWorkId}
                        feedbackAt={row.feedbackAt}
                        works={works}
                        onSaved={applyAllocationFeedback}
                        onError={setFormError}
                      />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => removeAllocationRow(index)}
                      aria-label="Remover obra"
                      title="Remover esta linha"
                      className="px-2 py-1 border border-border rounded text-secondary-text text-sm"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      ✕
                    </button>
                  </span>
                </div>

                {/* Sem obra atribuída, o utilizador escolhe entre criar a obra (default quando o
                    documento refere uma que não existe) ou apanhar uma existente — o switch troca. */}
                {!row.workId && !row.isStructural ? (
                  <ToggleSwitch
                    checked={row.createMode}
                    onChange={(next) =>
                      updateAllocation(index, {
                        createMode: next,
                        createError: null,
                        newWorkName: row.newWorkName || row.suggestedWorkName,
                      })
                    }
                    label="Criar obra nova"
                  />
                ) : null}

                {row.createMode && !row.workId && !row.isStructural ? (
                  <>
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex flex-col gap-1 flex-1" style={{ minWidth: "12rem" }}>
                        <span className="text-xs text-secondary-text">Nome da obra</span>
                        <input
                          type="text"
                          value={row.newWorkName}
                          onChange={(event) =>
                            updateAllocation(index, { newWorkName: event.target.value })
                          }
                          className="px-3 py-2 border border-border rounded bg-background text-foreground"
                          style={{ borderRadius: "var(--radius-button)" }}
                        />
                      </label>
                      <SearchableSelect<string>
                        className="flex-1"
                        style={{ minWidth: "12rem" }}
                        label="Cliente (dono da obra)"
                        required
                        labelClassName="text-xs text-secondary-text"
                        labelStyle={{}}
                        value={
                          row.newWorkClientId === "" ? null : row.newWorkClientId
                        }
                        onChange={(next) =>
                          updateAllocation(index, { newWorkClientId: next ?? "" })
                        }
                        options={clientSelectOptions}
                        loading={clientsLoading}
                        placeholder="— Selecionar cliente —"
                        searchPlaceholder="Procurar cliente…"
                        panelPosition="fixed"
                      />
                      <label className="flex flex-col gap-1" style={{ width: "8rem" }}>
                        <span className="text-xs text-secondary-text">Valor s/IVA</span>
                        <input
                          type="number"
                          step="0.01"
                          value={row.amount}
                          onChange={(event) =>
                            updateAllocation(index, { amount: event.target.value })
                          }
                          className="px-3 py-2 border border-border rounded bg-background text-foreground"
                          style={{ borderRadius: "var(--radius-button)" }}
                        />
                      </label>
                    </div>
                    {row.createError ? (
                      <span className="text-xs text-destructive">{row.createError}</span>
                    ) : null}
                    <span className="text-xs text-secondary-text">
                      A obra é criada ao guardar — código automático, estado inicial "Adjudicada".
                    </span>
                  </>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <SearchableSelect<string>
                      className="flex-1"
                      style={{ minWidth: "12rem" }}
                      label="Obra"
                      labelClassName="text-xs text-secondary-text"
                      labelStyle={{}}
                      value={
                        row.isStructural
                          ? STRUCTURAL_OPTION
                          : row.workId === ""
                            ? null
                            : row.workId
                      }
                      onChange={(next) => {
                        if (next === STRUCTURAL_OPTION) {
                          updateAllocation(index, { workId: "", isStructural: true });
                          return;
                        }
                        updateAllocation(index, {
                          workId: next ?? "",
                          isStructural: false,
                        });
                      }}
                      options={allocationWorkOptions}
                      placeholder="— Selecionar obra —"
                      searchPlaceholder="Procurar obra…"
                      panelPosition="fixed"
                    />
                    <label className="flex flex-col gap-1" style={{ width: "8rem" }}>
                      <span className="text-xs text-secondary-text">Valor s/IVA</span>
                      <input
                        type="number"
                        step="0.01"
                        value={row.amount}
                        onChange={(event) =>
                          updateAllocation(index, { amount: event.target.value })
                        }
                        className="px-3 py-2 border border-border rounded bg-background text-foreground"
                        style={{ borderRadius: "var(--radius-button)" }}
                      />
                    </label>
                  </div>
                )}

                <label className="flex flex-col gap-1">
                  <span className="text-xs text-secondary-text">Descritivo</span>
                  <textarea
                    rows={2}
                    value={row.description}
                    onChange={(event) =>
                      updateAllocation(index, { description: event.target.value })
                    }
                    className="px-3 py-2 border border-border rounded bg-background text-foreground"
                    style={{
                      borderRadius: "var(--radius-button)",
                      resize: "vertical",
                    }}
                  />
                </label>
              </div>
            ))}

            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={addAllocationRow}
                  className="px-3 py-2 border border-border rounded text-foreground text-sm"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  + Adicionar obra
                </button>
                <button
                  type="button"
                  onClick={distributeEvenly}
                  className="px-3 py-2 border border-border rounded text-foreground text-sm"
                  style={{ borderRadius: "var(--radius-button)" }}
                >
                  Repartir igualmente
                </button>
              </div>
              <span
                className={`text-sm ${remainder !== null && Math.abs(remainder) >= 0.01 ? "text-destructive" : "text-secondary-text"}`}
              >
                Atribuído {formatCurrency(allocatedSum)}
                {netTotal != null
                  ? ` / ${formatCurrency(netTotal)} · Resto ${formatCurrency(remainder ?? 0)}`
                  : ""}
              </span>
            </div>
          </div>
        )}
      </div>

      <SearchableSelect<string>
        label="Fornecedor / Sub-empreiteiro (opcional)"
        labelClassName="text-sm text-secondary-text"
        labelStyle={{}}
        value={
          form.matchedSupplierId ||
          (matchedSubcontractor ? `sub:${matchedSubcontractor.id}` : "")
        }
        onChange={(next) => {
          const value = next ?? "";
          if (value.startsWith("sub:")) {
            const subcontractor = subcontractorChoices.find(
              (candidate) => `sub:${candidate.id}` === value,
            );
            setForm((prev) => ({
              ...prev,
              // Sub-empreiteiro não tem registo em Fornecedores: entra como
              // texto livre pré-preenchido (nome + NIF), editável abaixo.
              matchedSupplierId: "",
              supplierName: subcontractor?.name ?? prev.supplierName,
              supplierTaxNumber: subcontractor?.taxId ?? "",
              createSupplier: false,
            }));
            return;
          }
          setForm((prev) => ({
            ...prev,
            matchedSupplierId: value,
            // Fornecedor fixo escolhido → limpar o texto livre e a opção de inserir.
            supplierName: value ? "" : prev.supplierName,
            supplierTaxNumber: value ? "" : prev.supplierTaxNumber,
            createSupplier: value ? false : prev.createSupplier,
          }));
        }}
        options={supplierSelectOptions}
        allowClear={false}
        searchPlaceholder="Procurar fornecedor…"
        panelPosition="fixed"
      />

      {!form.matchedSupplierId && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-secondary-text">
            Nome do fornecedor
          </span>
          <input
            type="text"
            value={form.supplierName}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                supplierName: event.target.value,
              }))
            }
            placeholder="Nome livre do fornecedor"
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </label>
      )}

      {!form.matchedSupplierId && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-secondary-text">
            NIF do fornecedor
          </span>
          <input
            type="text"
            value={form.supplierTaxNumber}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                supplierTaxNumber: event.target.value,
              }))
            }
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </label>
      )}

      {!form.matchedSupplierId && form.supplierName.trim().length > 0 && (
        <label className="flex items-center gap-2 md:col-span-2 text-sm text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={form.createSupplier}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                createSupplier: event.target.checked,
              }))
            }
          />
          <span>Inserir este fornecedor na tabela de fornecedores</span>
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-sm text-secondary-text">
          Data do Documento
        </span>
        <input
          type="date"
          value={form.documentDate}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              documentDate: event.target.value,
            }))
          }
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)" }}
        />
      </label>

      {/* Só na edição: um registo novo é uma previsão de gasto — o documento (e o seu nº) ainda
          não existe; pedir o número aqui era pedir um dado que o cliente não tem (feedback). */}
      {editingId != null && (
        <label className="flex flex-col gap-1">
          <span className="text-sm text-secondary-text">Nº documento</span>
          <input
            type="text"
            value={form.documentNumber}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                documentNumber: event.target.value,
              }))
            }
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </label>
      )}

      <label className="flex flex-col gap-1 md:col-span-2">
        <span className="text-sm text-secondary-text">Descritivo</span>
        <textarea
          rows={3}
          value={form.description}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              description: event.target.value,
            }))
          }
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)", resize: "vertical" }}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-secondary-text">Valor s/IVA *</span>
        <input
          type="number"
          step="0.01"
          value={form.baseAmount}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              baseAmount: event.target.value,
            }))
          }
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)" }}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-secondary-text">Valor c/IVA *</span>
        <input
          type="number"
          step="0.01"
          value={form.totalAmount}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              totalAmount: event.target.value,
            }))
          }
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)" }}
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-sm text-secondary-text">Valor pago</span>
        <input
          type="number"
          step="0.01"
          value={form.paidAmount}
          placeholder="Lido do comprovativo"
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              paidAmount: event.target.value,
            }))
          }
          className="px-3 py-2 border border-border rounded bg-background text-foreground"
          style={{ borderRadius: "var(--radius-button)" }}
        />
      </label>
    </>
  );

  // Obra do filtro de coluna, como número — as linhas repartidas mostram a fatia desta obra.
  const filteredWorkId = workFilter ? Number(workFilter) : null;

  // Linha da seleção: os valores mostrados (fatia, se há filtro de obra).
  const lineFor = (invoice: Invoice): SelectedInvoiceLine => {
    const amounts = rowAmounts(invoice, filteredWorkId);
    return {
      id: invoice.id,
      base: amounts.base ?? 0,
      total: amounts.total ?? 0,
    };
  };

  // Seleção manual de linhas com total próprio (à Excel), sobre a listagem atual — mudar os
  // filtros retira as linhas que saíram do resultado (ver InvoiceSelection.tsx).
  const selection = useInvoiceSelection(invoices.map(lineFor));

  // Só quando a seleção inteira já está fora do Resumo é que o gesto de grupo se inverte.
  const selectionAllExcluded =
    selection.count > 0 &&
    invoices
      .filter((invoice) => selection.ids.includes(invoice.id))
      .every((invoice) => invoice.excludeFromSummary);

  const handleSetSelectedSummaryExclusion = async (excluded: boolean) => {
    const ids = selection.ids;
    if (ids.length === 0 || isTogglingSelectionExclusion) return;
    setIsTogglingSelectionExclusion(true);
    setActionError(null);
    try {
      await setInvoicesSummaryExclusion(ids, excluded);
      setInvoices((prev) =>
        prev.map((item) =>
          ids.includes(item.id) ? { ...item, excludeFromSummary: excluded } : item,
        ),
      );
      setDocInvoice((current) =>
        current && ids.includes(current.id)
          ? { ...current, excludeFromSummary: excluded }
          : current,
      );
      setActionNotice(
        excluded
          ? `${ids.length} linha(s) fora do Resumo do ano`
          : `${ids.length} linha(s) voltam a contar no Resumo do ano`,
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsTogglingSelectionExclusion(false);
    }
  };

  // Por pagar e não NC: as únicas que fazem sentido combinar (o servidor aplica a mesma regra).
  const payableSelection = invoices.filter(
    (invoice) =>
      selection.ids.includes(invoice.id) &&
      invoice.approvalStatusCode !== "pago" &&
      !invoice.isCreditNote &&
      !invoice.isAnnulledByCreditNote,
  );

  const handleSchedulePayment = async (isoDate: string | null) => {
    const ids = payableSelection.map((invoice) => invoice.id);
    if (ids.length === 0 || isSchedulingPayment) return;
    setIsSchedulingPayment(true);
    setActionError(null);
    try {
      const result = await scheduleInvoicePayment(ids, isoDate);
      await loadInvoices();
      window.dispatchEvent(new Event("aja2:tasks"));
      const parts: string[] = [];
      if (isoDate === null) {
        parts.push(`${result.updated} fatura(s) voltam ao pagamento normal`);
      } else {
        const date = new Date(`${isoDate}T00:00:00`).toLocaleDateString(APP_LOCALE);
        parts.push(
          result.tasks.length === 1
            ? `Tarefa criada: ${result.tasks[0].title}, até ${date}`
            : `${result.tasks.length} tarefas de pagamento criadas, até ${date}`,
        );
      }
      if (result.skipped > 0) parts.push(`${result.skipped} já paga(s) ficaram de fora`);
      const notice = parts.join(" · ");
      setActionNotice(notice);
      setTasksLinkNotice(isoDate === null ? null : notice);
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsSchedulingPayment(false);
    }
  };

  const handleExport = async (format: "excel" | "csv") => {
    setIsExportMenuOpen(false);
    setIsExporting(true);
    try {
      // Os mesmos filtros da listagem — o ficheiro é "a grelha tal como está", mas inteira.
      // Com linhas marcadas, exporta só essas.
      await exportInvoices(
        activeFilters,
        format,
        selection.count > 0 ? selection.ids : undefined,
      );
    } catch (error) {
      setActionError(parseErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  };

  const actions = (
    <div className="flex items-center gap-2">
      <button
        className="px-4 py-2 border border-border rounded text-foreground flex items-center gap-2"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={openCreate}
      >
        <Add20Regular className="w-5 h-5" />
        Criar registo
      </button>
      <button
        className="px-4 py-2 bg-primary text-primary-foreground rounded transition-colors flex items-center gap-2"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => docInputRef.current?.click()}
      >
        <ArrowUpload20Regular className="w-5 h-5" />
        Subir Documento de Despesa
      </button>
      <input
        ref={docInputRef}
        type="file"
        accept={EXPENSE_DOCUMENT_ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          handleExtractUpload(event.target.files);
          event.target.value = "";
        }}
      />
      {/* Comprovativo que paga várias faturas: entra por aqui, não por uma linha. */}
      <button
        className="px-4 py-2 border border-border rounded transition-colors flex items-center gap-2 text-foreground hover:bg-accent/30 disabled:opacity-50"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        disabled={proofBusy}
        onClick={() => proofInputRef.current?.click()}
        title="Carregue um comprovativo que pagou várias faturas e o sistema procura quais foram."
      >
        <Receipt20Regular className="w-5 h-5" />
        {proofBusy ? "A ler…" : "Comprovativo de várias faturas"}
      </button>
      <input
        ref={proofInputRef}
        type="file"
        accept={EXPENSE_DOCUMENT_ACCEPT}
        className="hidden"
        onChange={(event) => {
          handleProofUpload(event.target.files);
          event.target.value = "";
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Faturas Despesas"
        subtitle="Faturas de despesa da empresa — gerais/estrutura e diretas a obras"
        actions={actions}
      />

      {/* O que o número do menu conta e o que fazer para ele descer. */}
      {pendingValidationCount !== null && pendingValidationCount > 0 && (
        <div
          className="px-4 py-2 border-b border-border text-sm flex flex-wrap items-center gap-2"
          style={{ backgroundColor: "rgba(180,83,9,0.08)" }}
        >
          <span className="font-medium" style={{ color: "#b45309" }}>
            {pendingValidationCount} por validar
          </span>
          <span className="text-secondary-text">
            {onlyPendingValidation
              ? "Confere cada uma (valores, obra, fornecedor) e carrega no ✓ da linha (ou Guardar e validar na ficha). Para várias de uma vez, seleciona-as e usa Validar seleção. Depois de juntar uma fatura ao auto fica uma só linha para validar."
              : "Entraram da leitura automática e ninguém as confirmou. É isto que o número do menu conta."}
          </span>
          {!onlyPendingValidation && (
            <button
              type="button"
              className="hover:underline"
              style={{ color: "var(--primary)" }}
              onClick={() => setOnlyPendingValidation(true)}
            >
              Ver só estas
            </button>
          )}
        </div>
      )}

      <div className="p-4 border-b border-border bg-card flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
          <input
            type="text"
            placeholder="Procurar por fornecedor ou valor..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
            style={{ borderRadius: "var(--radius-button)" }}
          />
        </div>

        {/* Opções por extenso — «Só estrutura»/«Só de obra» sem contexto deixou o cliente sem
            saber para que servia (feedback da importação de faturas). */}
        <Tooltip text="Separa as despesas de estrutura (gerais da empresa, sem obra) das despesas afetas a obras.">
          <select
            value={structural}
            onChange={(event) =>
              setStructural(event.target.value as StructuralFilter)
            }
            className="px-3 py-2 border border-border rounded bg-background text-foreground"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            <option value="all">Estrutura e obras</option>
            <option value="structural">Só estrutura (despesas gerais)</option>
            <option value="work">Só despesas de obras</option>
          </select>
        </Tooltip>

        {/* Lista só as linhas marcadas "fora do Resumo" — sem isto só se encontravam uma a
            uma, à procura do badge no meio da lista. */}
        <button
          type="button"
          aria-pressed={onlyExcludedFromSummary}
          onClick={() => setOnlyExcludedFromSummary((prev) => !prev)}
          className={`px-3 py-2 text-sm border cursor-pointer ${
            onlyExcludedFromSummary
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-background text-foreground hover:bg-accent/30"
          }`}
          style={{ borderRadius: "var(--radius-button)" }}
        >
          Fora do Resumo
        </button>

        {/* As "por validar" são o número do menu: entraram da leitura e ninguém as confirmou.
            Filtrar, selecionar e "Validar seleção" é o caminho para o número descer. */}
        <Tooltip text="Só as faturas por validar — as que o número do menu conta. Valida no ✓ de cada linha ou seleciona várias e usa Validar seleção.">
          <button
            type="button"
            aria-pressed={onlyPendingValidation}
            onClick={() => setOnlyPendingValidation((prev) => !prev)}
            className={`px-3 py-2 text-sm border cursor-pointer ${
              onlyPendingValidation
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background text-foreground hover:bg-accent/30"
            }`}
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Por validar
          </button>
        </Tooltip>

        {/* Categoria, obra, fornecedor e estado filtram-se na respetiva coluna da tabela
            (estilo Excel) — aqui ficam só os que não têm coluna própria. O botão aparece também
            quando há texto na barra de pesquisa e, ao limpar, apaga-a com o resto. */}
        {(hasColumnFilters || search) && (
          <button
            type="button"
            onClick={() => {
              clearColumnFilters();
              setSearch("");
            }}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            Limpar filtros
          </button>
        )}

        {/* Validar em grupo: só aparece quando a seleção tem pendentes. */}
        {selection.count > 0 &&
          invoices.some(
            (invoice) =>
              selection.ids.includes(invoice.id) &&
              invoice.validationStatus === "PendingValidation",
          ) && (
          <button
            type="button"
            onClick={() => void handleValidateSelected()}
            disabled={isValidatingSelection || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)", color: "#15803d" }}
          >
            {isValidatingSelection ? "A validar…" : `Validar seleção (${selection.count})`}
          </button>
        )}

        {/* Fora do Resumo em grupo: quando a seleção inteira já está fora, o gesto inverte-se. */}
        {selection.count > 0 && (
          <button
            type="button"
            onClick={() => void handleSetSelectedSummaryExclusion(!selectionAllExcluded)}
            disabled={isTogglingSelectionExclusion || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {isTogglingSelectionExclusion
              ? "A marcar…"
              : selectionAllExcluded
                ? `Contar no Resumo (${selection.count})`
                : `Fora do Resumo (${selection.count})`}
          </button>
        )}

        {/* Pagamento combinado com o fornecedor: só com faturas por pagar na seleção. */}
        {payableSelection.length > 0 && (
          <PaymentDateMenu
            count={payableSelection.length}
            hasPlanned={payableSelection.some((invoice) => invoice.plannedPaymentDate)}
            busy={isSchedulingPayment}
            disabled={isLoading}
            onPick={(isoDate) => void handleSchedulePayment(isoDate)}
          />
        )}

        {/* Apagar a seleção vive ao lado do exportar: as duas ações de grupo da mesma seleção. */}
        {selection.count > 0 && (
          <button
            type="button"
            onClick={() => void handleDeleteSelected()}
            disabled={isDeletingSelection || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background hover:bg-accent/30 disabled:opacity-50"
            style={{ borderRadius: "var(--radius-button)", color: "var(--destructive)" }}
          >
            {isDeletingSelection
              ? "A apagar…"
              : `Apagar seleção (${selection.count})`}
          </button>
        )}

        {/* Um só botão de exportação; o formato (Excel/CSV) escolhe-se no menu. Sem seleção
            exporta o conjunto filtrado inteiro (não só a página); com linhas marcadas, só essas.
            Vive junto dos filtros para ficar claro que o ficheiro é a vista tal como está. */}
        <div className="relative" ref={exportMenuRef}>
          <button
            type="button"
            onClick={() => setIsExportMenuOpen((prev) => !prev)}
            disabled={isExporting || isLoading}
            className="px-3 py-2 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50 flex items-center gap-1"
            style={{ borderRadius: "var(--radius-button)" }}
            aria-haspopup="menu"
            aria-expanded={isExportMenuOpen}
          >
            <ArrowDownload20Regular className="w-4 h-4" />
            {isExporting
              ? "A exportar…"
              : selection.count > 0
                ? `Exportar seleção (${selection.count})`
                : "Exportar"}
            <ChevronDown20Regular className="w-4 h-4 text-secondary-text" />
          </button>
          {isExportMenuOpen && (
            <div
              className="absolute right-0 mt-1 bg-card border border-border shadow-lg z-50"
              style={{ borderRadius: "var(--radius-card)", minWidth: "10rem" }}
            >
              <button
                type="button"
                onClick={() => handleExport("excel")}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent text-foreground"
              >
                Excel (.xlsx)
              </button>
              <button
                type="button"
                onClick={() => handleExport("csv")}
                className="w-full px-4 py-2 text-left text-sm hover:bg-accent text-foreground"
              >
                CSV (.csv)
              </button>
            </div>
          )}
        </div>
      </div>

      {actionError && (
        <div
          className="px-4 py-2 border-b border-border flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <span className="break-all">{actionError}</span>
          <Tooltip text="Dispensar">
            <button
              className="ml-auto p-1 rounded hover:bg-accent/30"
              onClick={() => setActionError(null)}
            >
              <Dismiss20Regular className="w-4 h-4" />
            </button>
          </Tooltip>
        </div>
      )}

      {/* Região viva sempre montada: se só nascesse com a mensagem, o leitor de ecrã não a
          anunciava — e o aviso pode ficar tapado pela modal aberta, o anúncio é a garantia. */}
      <div role="status">
        {actionNotice && (
          <div
            className="px-4 py-2 border-b border-border flex items-center gap-2 text-sm"
            style={{ color: "var(--primary)" }}
          >
            <CheckmarkCircle20Regular className="w-5 h-5 flex-shrink-0" />
            <span className="break-all">{actionNotice}</span>
            {tasksLinkNotice === actionNotice && (
              <Link to="/tarefas" className="underline whitespace-nowrap">
                Ver nas Tarefas
              </Link>
            )}
            <Tooltip text="Dispensar">
              <button
                className="ml-auto p-1 rounded hover:bg-accent/30"
                onClick={() => setActionNotice(null)}
              >
                <Dismiss20Regular className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        )}
      </div>

      {jobs.length > 0 && (
        <div className="px-4 py-3 border-b border-border flex flex-col gap-2">
          {jobs.map((job) => {
            const processing =
              job.processingStatus === "Pending" ||
              job.processingStatus === "Processing";
            const failed = job.processingStatus === "Failed";
            // Pronto mas sem fatura = descartado (ex.: duplicado) — dizer porquê, senão o
            // utilizador via "Fatura criada" e ia procurá-la em vão.
            const duplicate =
              job.processingStatus === "Ready" &&
              job.classification === "Duplicate";
            const paymentProof =
              job.processingStatus === "Ready" &&
              job.classification === "PaymentProof";
            // Cartão de mensagem com fundo destacado, distinto da tabela.
            const accent = failed
              ? "var(--destructive)"
              : duplicate
                ? "#b45309"
                : paymentProof
                  ? "#0369a1"
                  : "var(--primary)";
            const cardStyle = {
              borderRadius: "var(--radius)",
              backgroundColor:
                failed || duplicate || paymentProof ? "var(--muted)" : "var(--accent)",
              borderColor: accent,
            };
            const duplicateIds = duplicate ? (job.duplicateOfInvoiceIds ?? []) : [];
            const statusText = failed
              ? `Falhou${job.processingError ? `: ${job.processingError}` : ""}`
              : duplicate
                ? `Não criou fatura — ${job.classificationReason ?? "conteúdo igual a um documento já importado"}.${
                    duplicateIds.length > 1
                      ? ` Já existem ${duplicateIds.length} faturas com este conteúdo.`
                      : ""
                  }`
                : paymentProof
                  ? "Não criou despesa: é um comprovativo de pagamento, não uma fatura. Fica no painel de Importações, onde pode ser marcado como fatura se for engano."
                  : job.processingStatus === "Ready"
                    ? "Fatura criada — por validar."
                    : job.processingStatus === "Pending"
                      ? "Em fila para processamento…"
                      : "A ler o documento com o Document Intelligence…";
            const duplicateCursor = duplicateCursors[job.documentId] ?? 0;
            return (
              <div
                key={job.documentId}
                className="flex items-center gap-3 px-4 py-3 border text-sm"
                style={cardStyle}
              >
                {processing ? (
                  <ArrowSync20Regular
                    className="w-5 h-5 animate-spin"
                    style={{ color: accent, flexShrink: 0 }}
                  />
                ) : failed || duplicate ? (
                  <ErrorCircle20Regular
                    className="w-5 h-5"
                    style={{ color: accent, flexShrink: 0 }}
                  />
                ) : (
                  <CheckmarkCircle20Regular
                    className="w-5 h-5"
                    style={{ color: accent, flexShrink: 0 }}
                  />
                )}
                <div className="flex flex-col min-w-0">
                  <span className="font-medium text-foreground break-all">
                    {job.fileName}
                  </span>
                  <span style={{ color: accent }}>{statusText}</span>
                </div>
                {duplicate && (
                  <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                    {duplicateIds.length > 0 && (
                      <button
                        type="button"
                        className="px-3 py-1.5 text-sm border bg-background text-foreground hover:bg-accent/30"
                        style={{
                          borderRadius: "var(--radius-button)",
                          borderColor: accent,
                        }}
                        onClick={() => showNextDuplicate(job.documentId, duplicateIds)}
                      >
                        {/* Com várias cópias o botão diz quantas são e em qual vai — clicar outra
                            vez avança para a seguinte e volta ao início no fim. */}
                        {duplicateIds.length === 1
                          ? "Mostrar a fatura existente"
                          : duplicateCursor === 0
                            ? `Mostrar existentes (${duplicateIds.length})`
                            : `Mostrar existentes (${duplicateCursor} de ${duplicateIds.length})`}
                      </button>
                    )}
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm border border-border bg-background text-foreground hover:bg-accent/30"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() => importDuplicateAnyway(job.documentId)}
                    >
                      Importar na mesma
                    </button>
                  </div>
                )}
                {!processing && (
                  <Tooltip text="Dispensar">
                    <button
                      className={`${duplicate ? "" : "ml-auto "}p-1 rounded hover:bg-accent/30 text-secondary-text`}
                      onClick={() => dismissJob(job.documentId)}
                    >
                      <Dismiss20Regular className="w-4 h-4" />
                    </button>
                  </Tooltip>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoading && !hasLoadedOnce && (
          <div className="p-8 text-center text-secondary-text">
            A carregar faturas...
          </div>
        )}

        {!isLoading && loadError && (
          <div className="p-8 text-center" style={{ color: "var(--destructive)" }}>
            {loadError}
          </div>
        )}

        {/* A tabela desenha-se mesmo sem linhas: é no cabeçalho que vivem os filtros, e escondê-la
            ao filtrar para zero resultados deixava o utilizador sem forma de os limpar. Nos
            reloads seguintes fica montada (só esmaece) — desmontá-la matava o foco dos inputs de
            filtro e fechava os popovers a cada tecla, com o debounce a disparar a meio da escrita. */}
        {(hasLoadedOnce || !isLoading) && !loadError && (
          <table
            className="w-full text-sm"
            aria-busy={isLoading}
            style={{ opacity: isLoading ? 0.6 : 1 }}
          >
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-secondary-text">
                <th className="px-3 py-2 font-medium" style={{ width: 36 }}>
                  <Tooltip text="Selecionar as linhas visíveis">
                    <input
                      type="checkbox"
                      checked={selection.allSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            !selection.allSelected && selection.anySelected;
                        }
                      }}
                      onChange={selection.toggleAll}
                    />
                  </Tooltip>
                </th>
                <th className="px-3 py-2 font-medium">Empresa</th>
                <th className="px-3 py-2 font-medium">Categoria</th>
                <th className="px-3 py-2 font-medium">Sub-tipo</th>
                <th className="px-3 py-2 font-medium">Obra</th>
                <th className="px-3 py-2 font-medium">Fornecedor</th>
                <SortHeader
                  label="Data"
                  active={sort === "documento"}
                  ascending={sortAscending}
                  title="Ordenar pela data impressa no documento. Clicar outra vez inverte."
                  onClick={() => toggleSort("documento")}
                />
                <SortHeader
                  label="Entrada"
                  active={sort === "entrada"}
                  ascending={sortAscending}
                  title="Quando a fatura entrou no sistema. Põe em cima o que acabou de chegar, mesmo que o documento tenha data antiga. Clicar outra vez inverte."
                  onClick={() => toggleSort("entrada")}
                />
                <th className="px-3 py-2 font-medium">Nº doc.</th>
                <th className="px-3 py-2 font-medium">Descritivo</th>
                <th className="px-3 py-2 font-medium text-right">s/IVA</th>
                <th className="px-3 py-2 font-medium text-right">c/IVA</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Ciclo</th>
                <th className="px-3 py-2 font-medium">Documentos</th>
                <th className="px-3 py-2 font-medium text-right">Ações</th>
              </tr>
              {/* Filtros por coluna (Excel): cada um vai à query, para o total do rodapé
                  acompanhar sempre o que está filtrado. */}
              <tr className="text-left">
                <ColumnFilterEmpty />
                <ColumnFilterSelect
                  value={companyFilter}
                  label="Empresa"
                  onChange={setCompanyFilter}
                  allLabel="Todas"
                  title="Empresa do grupo. Cada fatura pertence à empresa que consta do documento como adquirente."
                  options={companies.map((company) => ({
                    value: String(company.id),
                    label: company.name,
                  }))}
                />
                <th className="px-3 pb-2 font-normal align-top">
                  <SearchableSelect<string>
                    ariaLabel="Categoria"
                    value={categoryFilter}
                    onChange={(next) => setCategoryFilter(next ?? "")}
                    options={categoryFilterSelectOptions}
                    allowClear={false}
                    searchPlaceholder="Procurar…"
                    panelPosition="fixed"
                    className={categoryFilter ? "font-medium" : undefined}
                    style={
                      {
                        minWidth: "6.5rem",
                        "--text-base": "var(--text-caption)",
                        ...(categoryFilter
                          ? { "--border": "var(--primary)" }
                          : null),
                      } as CSSProperties
                    }
                  />
                </th>
                {/* Sub-tipo passa a seletor com pesquisa (escrever) — a lista de sub-tipos pode ser
                    longa, como a Categoria/Fornecedor. Mesmo desenho compacto por tokens. */}
                <th className="px-3 pb-2 font-normal align-top">
                  <SearchableSelect<string>
                    ariaLabel="Sub-tipo"
                    value={subTypeFilter}
                    onChange={(next) => setSubTypeFilter(next ?? "")}
                    options={subTypeFilterSelectOptions}
                    allowClear={false}
                    searchPlaceholder="Procurar…"
                    panelPosition="fixed"
                    className={subTypeFilter ? "font-medium" : undefined}
                    style={
                      {
                        minWidth: "6.5rem",
                        "--text-base": "var(--text-caption)",
                        ...(subTypeFilter
                          ? { "--border": "var(--primary)" }
                          : null),
                      } as CSSProperties
                    }
                  />
                </th>
                {/* Obra e Fornecedor são as colunas de cardinalidade alta — seletor com pesquisa
                    em vez do <select> nativo. Compacto via tokens (o CSS é pré-compilado, classes
                    novas não existem): o tamanho de letra desce para o dos restantes filtros e,
                    com filtro ativo, a moldura fica primária (o input herda o token --border). */}
                <th className="px-3 pb-2 font-normal align-top">
                  <Tooltip text="Filtrar por obra. Nas faturas repartidas o total conta só a fatia desta obra.">
                    <div>
                      <SearchableSelect<string>
                        ariaLabel="Obra"
                        value={workFilter}
                        onChange={(next) => setWorkFilter(next ?? "")}
                        options={workFilterSelectOptions}
                        allowClear={false}
                        searchPlaceholder="Procurar…"
                        panelPosition="fixed"
                        className={workFilter ? "font-medium" : undefined}
                        style={
                          {
                            minWidth: "6.5rem",
                            "--text-base": "var(--text-caption)",
                            ...(workFilter
                              ? { "--border": "var(--primary)" }
                              : null),
                          } as CSSProperties
                        }
                      />
                    </div>
                  </Tooltip>
                </th>
                <th className="px-3 pb-2 font-normal align-top">
                  <SearchableSelect<string>
                    ariaLabel="Fornecedor"
                    value={supplierFilter}
                    onChange={(next) => setSupplierFilter(next ?? "")}
                    options={supplierFilterSelectOptions}
                    allowClear={false}
                    searchPlaceholder="Procurar…"
                    panelPosition="fixed"
                    className={supplierFilter ? "font-medium" : undefined}
                    style={
                      {
                        minWidth: "6.5rem",
                        "--text-base": "var(--text-caption)",
                        ...(supplierFilter
                          ? { "--border": "var(--primary)" }
                          : null),
                      } as CSSProperties
                    }
                  />
                </th>
                <ColumnFilterDateRange
                  from={dateFromFilter}
                  to={dateToFilter}
                  onChange={(from, to) => {
                    setDateFromFilter(from);
                    setDateToFilter(to);
                  }}
                  title="Filtrar por data do documento (De/Até, inclusivo)."
                />
                <ColumnFilterDateRange
                  from={createdFromFilter}
                  to={createdToFilter}
                  onChange={(from, to) => {
                    setCreatedFromFilter(from);
                    setCreatedToFilter(to);
                  }}
                  title="Filtrar por data de entrada no sistema (De/Até, inclusivo) — quando a fatura cá chegou, não a data que traz impressa."
                />
                <ColumnFilterText
                  value={documentNumberFilter}
                  onChange={setDocumentNumberFilter}
                  placeholder="Contém…"
                />
                <ColumnFilterText
                  value={descriptionFilter}
                  onChange={setDescriptionFilter}
                  placeholder="Contém…"
                />
                <ColumnFilterAmount
                  value={netFilter}
                  onChange={setNetFilter}
                  title="Filtrar pelo valor s/IVA. Com filtro de obra compara o documento completo, não a fatia da obra."
                />
                <ColumnFilterAmount
                  value={grossFilter}
                  onChange={setGrossFilter}
                  title="Filtrar pelo valor c/IVA. Com filtro de obra compara o documento completo, não a fatia da obra."
                />
                <ColumnFilterSelect
                  value={statusFilter}
                  label="Estado"
                  onChange={setStatusFilter}
                  allLabel="Todos"
                  options={[
                    { value: "sem-estado", label: "Sem estado" },
                    ...statuses.map((status) => ({
                      value: status.code,
                      label: status.name,
                    })),
                  ]}
                />
                <ColumnFilterSelect
                  value={lifecycleFilter}
                  label="Ciclo"
                  onChange={setLifecycleFilter}
                  allLabel="Todos"
                  title="Fase do ciclo documental, derivada do que a despesa tem: prevista, com auto, com fatura, paga. Não se edita — acompanha os documentos."
                  options={[
                    { value: "prevista", label: "Prevista" },
                    { value: "com-auto", label: "Com auto" },
                    { value: "com-fatura", label: "Com fatura" },
                    { value: "paga", label: "Paga" },
                  ]}
                />
                <ColumnFilterSelect
                  value={documentsFilter}
                  label="Documentos"
                  onChange={setDocumentsFilter}
                  allLabel="Todos"
                  title="Filtrar pelos documentos anexos à fatura."
                  options={[
                    { value: "with-document", label: "Com fatura" },
                    { value: "without-document", label: "Sem fatura" },
                    { value: "with-payment-proof", label: "Com comprovativo" },
                    {
                      value: "without-payment-proof",
                      label: "Sem comprovativo",
                    },
                    { value: "split", label: "Autos (várias obras)" },
                  ]}
                />
                <ColumnFilterEmpty />
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={16} className="p-8 text-center text-secondary-text">
                    {hasColumnFilters || search
                      ? "Sem faturas para os filtros aplicados."
                      : "Sem faturas. Use “Nova fatura” para lançar a primeira."}
                  </td>
                </tr>
              )}
              {invoices.map((invoice, index) => {
                // Com filtro de obra, a linha de uma repartida mostra a fatia dessa obra (valores
                // e descritivo) — as linhas visíveis passam a somar o total do rodapé.
                const slice = sliceForWork(invoice, filteredWorkId);
                const amounts = rowAmounts(invoice, filteredWorkId);
                const description = rowDescription(invoice, filteredWorkId);
                return (
                <Fragment key={invoice.id}>
                <tr
                  id={`invoice-row-${invoice.id}`}
                  className={`cursor-pointer border-b border-border hover:bg-accent/30 ${
                    index % 2 === 1 ? "bg-muted" : ""
                  }`}
                  style={
                    highlight?.id === invoice.id
                      ? {
                          backgroundColor: "rgba(245,158,11,0.22)",
                          transition: "background-color 0.4s",
                        }
                      : undefined
                  }
                  onClick={() => openDocuments(invoice)}
                >
                  <td
                    className="px-3 py-2"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selection.isSelected(invoice.id)}
                      onChange={() => selection.toggle(invoice.id)}
                    />
                  </td>
                  <td className="px-3 py-2">{invoice.companyName ?? "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col gap-1">
                      <span>{categoryLabel(invoice)}</span>
                      {structuralBadge(invoice.isStructural)}
                    </div>
                  </td>
                  <td className="px-3 py-2">{invoice.subType ?? "—"}</td>
                  <td className="px-3 py-2">
                    {invoice.isSplit ? (
                      <span className="inline-flex items-center gap-1">
                        {/* Com filtro de obra, o nome da obra filtrada aparece logo na linha; o
                            chip fica só para abrir a repartição completa. */}
                        {slice && <span>{slice.workName ?? "—"}</span>}
                        <button
                          type="button"
                          className="text-xs flex-shrink-0"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "2px 8px",
                            borderRadius: "var(--radius-button)",
                            backgroundColor: "var(--accent)",
                            color: "var(--primary)",
                            cursor: "pointer",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            setExpandedInvoiceId((prev) =>
                              prev === invoice.id ? null : invoice.id,
                            );
                          }}
                        >
                          {splitWorkLabel(invoice)}
                          <span style={{ fontSize: "0.7em" }}>
                            {expandedInvoiceId === invoice.id ? "▲" : "▼"}
                          </span>
                        </button>
                      </span>
                    ) : (
                      (invoice.workName ?? "—")
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {/* `items-start` alinha o badge pela primeira linha quando o nome do
                        fornecedor quebra; `flex-shrink-0` + `whitespace-nowrap` impedem que o
                        flex o esmague e parta o "✉ Email" em duas linhas. Nota: a classe
                        `shrink-0` não existe no index.css compilado — só `flex-shrink-0`. */}
                    <span className="inline-flex items-start gap-1">
                      {invoice.sourceChannel === "email" && (
                        <Tooltip
                          text={`Importado por email${invoice.sourceSender ? ` de ${invoice.sourceSender}` : ""}`}
                        >
                          <span
                            className="text-xs flex-shrink-0 whitespace-nowrap"
                            style={{
                              padding: "1px 6px",
                              borderRadius: "var(--radius-button)",
                              backgroundColor: "var(--accent)",
                              color: "var(--primary)",
                            }}
                          >
                            ✉ Email
                          </span>
                        </Tooltip>
                      )}
                      {/* A dica do rato não chega a um <span>: não recebe foco, e o `onFocus` do
                          Tooltip nunca dispara. Sem isto, de onde veio a fatura — e de quem — só
                          existia para quem usa rato. */}
                      {invoice.sourceChannel === "email" && (
                        <SrOnly>
                          {`Importado por email${invoice.sourceSender ? ` de ${invoice.sourceSender}` : ""}. `}
                        </SrOnly>
                      )}
                      {/* Sem nome, o NIF identifica quem emitiu — e é por ele que se descobre de
                          quem é a despesa. Vale sobretudo desde que a leitura passou a preferir
                          não devolver nome nenhum a arriscar o nome de outro papel da fotografia:
                          um traço aqui deixava a despesa órfã na lista. */}
                      {/* O aviso vive nesta coluna por ser aqui que o engano custa: um valor
                          trocado vê-se ao conferir, um fornecedor trocado esconde a despesa de
                          quem a procura. Só aparece nas linhas lidas por máquina — as escritas à
                          mão não têm confiança nenhuma associada, nem precisam. */}
                      {invoice.confidence != null &&
                        invoice.confidence < CONFIANCA_BAIXA && (
                          <Tooltip
                            text={`Lido com pouca confiança (${Math.round(invoice.confidence * 100)}%) — confirme o fornecedor e os valores.`}
                          >
                            <Warning20Regular
                              className="w-4 h-4 flex-shrink-0"
                              // Token de tema e não uma cor escrita à mão: em modo escuro o âmbar
                              // que aqui estava dava 2,86:1 sobre as linhas alternadas, abaixo do
                              // mínimo de 3:1. O `--primary` acompanha o tema e passa nos dois; o
                              // que carrega o significado é a forma do triângulo, não a cor.
                              style={{ color: "var(--primary)" }}
                            />
                          </Tooltip>
                        )}
                      {/* O ícone do Fluent vai com `aria-hidden` por omissão e a dica do rato não
                          alcança um <svg>. Sem este texto, o aviso — que é a razão de ser desta
                          marca — não existia para quem navega por teclado ou leitor de ecrã. */}
                      {invoice.confidence != null &&
                        invoice.confidence < CONFIANCA_BAIXA && (
                          <SrOnly>
                            {`Lido com pouca confiança, ${Math.round(invoice.confidence * 100)}%: confirme o fornecedor e os valores. `}
                          </SrOnly>
                        )}
                      {invoice.matchedSupplierName ??
                        invoice.supplierName ??
                        (invoice.supplierTaxNumber
                          ? `NIF ${invoice.supplierTaxNumber}`
                          : "—")}
                      {/* Há uma previsão que pode ser esta fatura: o crachá abre a ficha, onde a
                          fusão se decide vendo as duas lado a lado. */}
                      {(invoice.forecastSuggestions?.length ?? 0) > 0 && (
                        <Tooltip text="Há despesas previstas que podem ser esta fatura — abra para decidir.">
                          <button
                            type="button"
                            className="text-xs flex-shrink-0 whitespace-nowrap"
                            style={{
                              padding: "1px 6px",
                              borderRadius: "var(--radius-button)",
                              backgroundColor: "var(--accent)",
                              color: "var(--foreground)",
                            }}
                            aria-label={`Fundir com previsão? Fatura ${
                              invoice.documentNumber ??
                              invoice.matchedSupplierName ??
                              invoice.supplierName ??
                              formatCurrency(invoice.totalAmount)
                            }`}
                            onClick={(event) => {
                              event.stopPropagation();
                              openDocuments(invoice);
                            }}
                          >
                            Fundir com previsão?
                          </button>
                        </Tooltip>
                      )}
                      {(invoice.forecastSuggestions?.length ?? 0) > 0 && (
                        <SrOnly>
                          {` (há ${invoice.forecastSuggestions!.length === 1 ? "uma despesa prevista que pode ser" : `${invoice.forecastSuggestions!.length} despesas previstas que podem ser`} esta fatura)`}
                        </SrOnly>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2">{formatDate(invoice.documentDate)}</td>
                  <Tooltip
                    text={`Entrou no sistema a ${new Date(invoice.created).toLocaleString(APP_LOCALE)}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatArrival(invoice.created)}
                      {/* "Hoje" e "Ontem" poupam a leitura a quem vê a tabela, mas escondiam a data
                          exacta de quem usa leitor de ecrã: ela só existia na dica do rato, que um
                          <td> não alcança pelo teclado. Aqui fica sempre, sem tomar espaço nem
                          acrescentar uma paragem de tabulação por linha. */}
                      <SrOnly>
                        {` (entrou a ${new Date(invoice.created).toLocaleString(APP_LOCALE)})`}
                      </SrOnly>
                    </td>
                  </Tooltip>
                  <td className="px-3 py-2">
                    {/* Dica no Tooltip da casa + gémeo SrOnly: a dica do rato num <span> não
                        recebe foco, e o "porquê fora do Resumo" (e a referência da NC) são
                        factos de negócio, não decoração — o mesmo padrão da pílula ✉ Email. */}
                    {invoice.isCreditNote && (
                      <>
                        <Tooltip text="Nota de crédito — fica fora do Resumo Financeiro">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                            style={{ color: "#b45309", backgroundColor: "rgba(245,158,11,0.10)" }}
                          >
                            NC
                          </span>
                        </Tooltip>
                        <SrOnly>{" Nota de crédito — fica fora do Resumo Financeiro. "}</SrOnly>
                      </>
                    )}
                    {invoice.isAnnulledByCreditNote && (
                      <>
                        <Tooltip
                          text={`Anulada por nota de crédito${invoice.creditNoteReference ? ` (${invoice.creditNoteReference})` : ""} — fica fora do Resumo Financeiro`}
                        >
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                            style={{ color: "var(--destructive)", backgroundColor: "rgba(220,38,38,0.08)" }}
                          >
                            Anulada
                          </span>
                        </Tooltip>
                        <SrOnly>
                          {` Anulada por nota de crédito${invoice.creditNoteReference ? ` (${invoice.creditNoteReference})` : ""} — fica fora do Resumo Financeiro. `}
                        </SrOnly>
                      </>
                    )}
                    {invoice.excludeFromSummary && (
                      <>
                        <Tooltip text="Não conta no Resumo do ano, por decisão manual. Muda-se na janela de Documentos.">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                            style={{ color: "var(--secondary-text)", backgroundColor: "rgba(100,116,139,0.12)" }}
                          >
                            Fora do Resumo
                          </span>
                        </Tooltip>
                        <SrOnly>{" Não conta no Resumo do ano, por decisão manual. "}</SrOnly>
                      </>
                    )}
                    {invoice.validationStatus === "PendingValidation" && (
                      <>
                        <Tooltip text="Por validar: entrou da leitura e ainda ninguém confirmou. Valida-se no ✓ da linha, com Guardar e validar na ficha, ou em grupo com a seleção.">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                            style={{ color: "#b45309", backgroundColor: "rgba(180,83,9,0.10)" }}
                          >
                            Por validar
                          </span>
                        </Tooltip>
                        <SrOnly>{" Por validar. "}</SrOnly>
                      </>
                    )}
                    {invoice.autoValidated && (
                      <>
                        <Tooltip text="Validada automaticamente: fatura com QR certificado da AT, fornecedor reconhecido pelo NIF e total coerente. Reversível com Invalidar na obra.">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded"
                            style={{ color: "#15803d", backgroundColor: "rgba(21,128,61,0.10)" }}
                          >
                            Validada auto.
                          </span>
                        </Tooltip>
                        <SrOnly>{" Validada automaticamente. "}</SrOnly>
                      </>
                    )}
                    {(invoice.isCreditNote || invoice.isAnnulledByCreditNote || invoice.excludeFromSummary ||
                      invoice.validationStatus === "PendingValidation" || invoice.autoValidated) && " "}
                    {invoice.documentNumber ?? "—"}
                  </td>
                  {/* Só quando há info extra (documento + fatia) — repetir o texto visível é ruído. */}
                  <Tooltip
                    text={
                      slice?.description && invoice.description
                        ? `${invoice.description} — ${slice.description}`
                        : null
                    }
                  >
                    <td className="px-3 py-2">
                      {/* O cap de largura vive no div: o algoritmo auto do <table> ignora
                          max-width nas células e deixaria a coluna crescer com o texto. */}
                      <div className="break-words" style={{ maxWidth: "18rem" }}>
                        {description ?? "—"}
                      </div>
                    </td>
                  </Tooltip>
                  <Tooltip
                    text={
                      amounts.isSlice
                        ? `Fatia desta obra · documento completo: ${formatCurrency(invoice.baseAmount)}`
                        : null
                    }
                  >
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(amounts.base)}
                    </td>
                  </Tooltip>
                  <Tooltip
                    text={
                      amounts.isSlice
                        ? `Fatia desta obra · documento completo: ${formatCurrency(invoice.totalAmount)}`
                        : null
                    }
                  >
                    <td className="px-3 py-2 text-right">
                      {formatCurrency(amounts.total)}
                    </td>
                  </Tooltip>
                  <td
                    className="px-3 py-2"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <select
                      value={invoice.approvalStatusCode}
                      onChange={(event) =>
                        handleStatusChange(invoice, event.target.value)
                      }
                      className="px-2 py-1 border border-border rounded bg-background text-foreground text-xs"
                      style={{ borderRadius: "var(--radius-button)" }}
                    >
                      {/* "sem-estado" é o sentinela do backend para NULL — escolher limpa o estado. */}
                      <option value="sem-estado">Sem estado</option>
                      {statuses.length === 0 &&
                        invoice.approvalStatusCode !== "sem-estado" && (
                          <option value={invoice.approvalStatusCode}>
                            {invoice.approvalStatusName}
                          </option>
                        )}
                      {statuses.map((status) => (
                        <option key={status.id} value={status.code}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                    {invoice.plannedPaymentDate &&
                      invoice.approvalStatusCode !== "pago" && (
                        <div
                          className="mt-1 text-xs text-secondary-text whitespace-nowrap"
                          title="Data combinada com o fornecedor"
                        >
                          Pagar a{" "}
                          {new Date(invoice.plannedPaymentDate).toLocaleDateString(APP_LOCALE, {
                            day: "2-digit",
                            month: "2-digit",
                            timeZone: "UTC",
                          })}
                        </div>
                      )}
                  </td>
                  <td className="px-3 py-2">
                    <CicloPill fase={invoice.lifecyclePhase} />
                  </td>
                  <td className="px-3 py-2">
                    {invoice.hasDocument || invoice.hasPaymentProof ? (
                      <div className="flex items-center gap-2">
                        {invoice.hasDocument && (
                          <Tooltip
                            // Folhas de cálculo não têm pré-visualização: descarregam-se como antes.
                            text={`${
                              isSpreadsheetName(invoice.documentFileName)
                                ? "Descarregar"
                                : "Ver"
                            } ${invoice.documentIsAuto ? "auto" : "fatura"}${
                              invoice.documentFileName
                                ? ` (${invoice.documentFileName})`
                                : ""
                            }`}
                          >
                            <button
                              className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-accent/30"
                              style={{ color: "var(--primary)" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (isSpreadsheetName(invoice.documentFileName)) {
                                  runDownload(
                                    downloadInvoiceDocument(invoice),
                                    setActionError,
                                  );
                                } else {
                                  setViewerDocument({
                                    name:
                                      invoice.documentFileName ??
                                      (invoice.documentIsAuto ? "Auto" : "Fatura"),
                                    load: () => fetchInvoiceDocument(invoice),
                                  });
                                }
                              }}
                            >
                              {isSpreadsheetName(invoice.documentFileName) ? (
                                <>
                                  <DocumentTable20Regular className="w-6 h-6" />
                                  <ArrowDownload20Regular className="w-4 h-4" />
                                </>
                              ) : (
                                <>
                                  <DocumentPdf20Regular className="w-6 h-6" />
                                  <Eye20Regular className="w-4 h-4" />
                                </>
                              )}
                            </button>
                          </Tooltip>
                        )}
                        {invoice.hasPaymentProof && (
                          <Tooltip
                            text={`Ver comprovativo${
                              invoice.paymentProofFileName
                                ? ` (${invoice.paymentProofFileName})`
                                : ""
                            }`}
                          >
                            <button
                              className="inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-accent/30"
                              style={{ color: "var(--primary)" }}
                              onClick={(event) => {
                                event.stopPropagation();
                                setViewerDocument({
                                  name: invoice.paymentProofFileName ?? "Comprovativo",
                                  load: () => fetchInvoicePaymentProof(invoice),
                                });
                              }}
                            >
                              <Receipt20Regular className="w-6 h-6" />
                              <Eye20Regular className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    ) : (
                      <span className="text-secondary-text">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {invoice.validationStatus === "PendingValidation" && (
                        <Tooltip text="Validar: os dados estão certos (valores, obra, fornecedor). Sai das por validar e o número do menu desce.">
                          <button
                            className="p-1 rounded hover:bg-accent disabled:opacity-50"
                            style={{ color: "#15803d" }}
                            aria-label="Validar"
                            disabled={validatingId === invoice.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleValidateOne(invoice);
                            }}
                          >
                            <Checkmark20Regular className="w-5 h-5" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip
                        text={
                          invoice.imputedCostCount > 0
                            ? `Imputado: ${formatCurrency(invoice.imputedAmount)} — clique para anular`
                            : "Imputar custo à(s) obra(s)"
                        }
                      >
                        <button
                          className="p-1 rounded hover:bg-accent text-sm"
                          style={{
                            color:
                              invoice.imputedCostCount > 0
                                ? "var(--primary)"
                                : "var(--secondary-text)",
                            fontWeight: "var(--font-weight-medium)",
                          }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleImpute(invoice, invoice.imputedCostCount === 0);
                          }}
                        >
                          {invoice.imputedCostCount > 0 ? "€ ✓" : "€"}
                        </button>
                      </Tooltip>
                      <Tooltip text="Editar">
                        <button
                          className="p-1 rounded hover:bg-accent"
                          onClick={(event) => {
                            event.stopPropagation();
                            openEdit(invoice);
                          }}
                        >
                          <Edit20Regular className="w-5 h-5" />
                        </button>
                      </Tooltip>
                      <Tooltip text="Apagar">
                        <button
                          className="p-1 rounded hover:bg-accent"
                          style={{ color: "var(--destructive)" }}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(invoice);
                          }}
                        >
                          <Delete20Regular className="w-5 h-5" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
                {invoice.isSplit && expandedInvoiceId === invoice.id && (
                  <tr className={index % 2 === 1 ? "bg-muted" : ""}>
                    <td
                      colSpan={16}
                      className="px-3"
                      style={{ paddingTop: 0, paddingBottom: "0.75rem" }}
                    >
                      <div
                        className="flex flex-col gap-1"
                        style={{ paddingLeft: "2rem", maxWidth: "48rem" }}
                      >
                        {/* Só o cabeçalho destacado (chip), não o bloco todo — em cinzento
                            pequeno perdia-se no meio das linhas da tabela. */}
                        <span
                          className="text-sm"
                          style={{
                            alignSelf: "flex-start",
                            padding: "2px 8px",
                            borderRadius: "var(--radius-button)",
                            backgroundColor: "var(--accent)",
                            color: "var(--primary)",
                            fontWeight: "var(--font-weight-medium)",
                          }}
                        >
                          Repartição por obra ({invoice.allocations.length}) ·{" "}
                          {formatCurrency(
                            invoice.allocations.reduce(
                              (sum, allocation) => sum + allocation.amount,
                              0,
                            ),
                          )}{" "}
                          s/IVA
                        </span>
                        {invoice.reappliedFromPreviousReview && (
                          <span className="text-xs" style={{ color: "#b45309" }}>
                            ↺ Distribuição reaplicada de uma revisão anterior — este documento já
                            tinha entrado e sido confirmado; verifique se não é um duplicado.
                          </span>
                        )}
                        {invoice.allocations.map((allocation) => (
                          <AllocationSummaryRow
                            key={allocation.id}
                            allocation={allocation}
                            works={works}
                            onFeedbackSaved={applyAllocationFeedback}
                            onFeedbackError={setActionError}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
            </tbody>
            {/* Totais do conjunto filtrado inteiro (vêm somados do servidor), não apenas das
                linhas desenhadas — a grelha pára nas 200 e a soma das visíveis enganaria. */}
            <tfoot className="sticky bottom-0 bg-card border-t-2 border-border font-medium">
              <SelectionTotalsRow
                selection={selection}
                labelColSpan={10}
                trailingColSpan={4}
              />
              <tr>
                <td className="px-3 py-2 text-secondary-text" colSpan={10}>
                  {totalsLabel(totals)}
                  {workFilter && " · só a fatia desta obra nas repartidas"}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatCurrency(totals.baseTotal)}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatCurrency(totals.grandTotal)}
                </td>
                <td className="px-3 py-2" colSpan={4} />
              </tr>
            </tfoot>
          </table>
        )}

        {!isLoading && !loadError && totalCount > invoices.length && (
          <div className="p-3 text-center text-secondary-text text-sm">
            A mostrar {invoices.length} de {totalCount}. Refine os filtros para ver mais.
          </div>
        )}
      </div>

      {/* Comprovativo de várias faturas: confirmar antes de liquidar */}
      {proofProposal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
          onMouseDown={pressBackdrop}
          onClick={(event) => releasedOnBackdrop(event) && setProofProposal(null)}
        >
          <div
            className="bg-card border border-border w-full max-w-3xl overflow-y-auto"
            // Tecto em estilo inline: `max-h-[85vh]` não existe no index.css compilado deste
            // projeto e era ignorado em silêncio — a janela crescia com as cem-e-tal faturas da
            // escolha à mão e saía do ecrã, levando com ela o cabeçalho e a explicação.
            style={{ borderRadius: "var(--radius-card)", maxHeight: "85vh" }}
          >
            <div className="px-5 py-4 border-b border-border">
              <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semi-bold)" }}>
                Comprovativo de várias faturas
              </h2>
              <p className="text-secondary-text text-sm break-all">{proofProposal.fileName}</p>
            </div>

            <div className="px-5 py-4 flex flex-col gap-4">
              {/* Sem proposta, isto deixa de ser uma nota e passa a ser a resposta: sem a ler, a
                  lista de faturas com caixas de selecção aparece sem se perceber porquê. */}
              {proofProposal.matches.length === 0 ? (
                <div
                  className="border px-3 py-2 text-sm"
                  style={{
                    borderRadius: "var(--radius)",
                    borderColor: "var(--primary)",
                    backgroundColor: "var(--accent)",
                  }}
                >
                  {proofProposal.summary}
                </div>
              ) : (
                <p className="text-sm">{proofProposal.summary}</p>
              )}

              {proofError && (
                <div
                  className="border border-border bg-destructive/10 text-destructive px-3 py-2 text-sm"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  {proofError}
                </div>
              )}

              {/* Linhas por beneficiário: quando existem, são a prova mais forte de que o conjunto
                  está certo — o comprovativo diz a quem pagou. */}
              {proofProposal.lines.length > 0 && (
                <div>
                  <div className="text-sm text-secondary-text mb-1">O comprovativo discrimina:</div>
                  <ul className="text-sm space-y-1">
                    {proofProposal.lines.map((line, index) => (
                      <li key={index}>
                        {line.beneficiary ?? "—"}
                        {line.amount != null && ` · ${formatCurrency(line.amount)}`}
                        {line.reference && ` · ${line.reference}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {proofProposal.matches.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="text-sm text-secondary-text">
                    {proofProposal.matches.length === 1
                      ? "Conjunto proposto:"
                      : "Escolha o conjunto correto:"}
                  </div>
                  {proofProposal.matches.map((match, index) => {
                    const ids = match.invoices.map((invoice) => invoice.invoiceId);
                    const isChosen =
                      ids.length === proofChosen.length &&
                      ids.every((id) => proofChosen.includes(id));
                    return (
                      <button
                        key={index}
                        type="button"
                        className="text-left border p-3 hover:bg-accent/20"
                        style={{
                          borderRadius: "var(--radius)",
                          borderColor: isChosen ? "var(--primary)" : "var(--border)",
                        }}
                        onClick={() => setProofChosen(ids)}
                      >
                        <div className="text-sm" style={{ fontWeight: "var(--font-weight-medium)" }}>
                          {match.invoices.length === 1
                            ? "1 fatura"
                            : `${match.invoices.length} faturas`}{" · "}
                          {formatCurrency(match.total)}
                        </div>
                        <div className="text-secondary-text" style={{ fontSize: "var(--text-xs)" }}>
                          {match.reason}
                        </div>
                        <ul className="mt-1 text-sm">
                          {match.invoices.map((invoice) => (
                            <li key={invoice.invoiceId}>
                              {invoice.supplierName ?? "—"}
                              {invoice.documentNumber && ` · ${invoice.documentNumber}`}
                              {" · "}
                              {formatCurrency(invoice.outstanding)}
                            </li>
                          ))}
                        </ul>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Escolha à mão: sempre disponível, e o único caminho quando nada fecha o valor. */}
              <details open={proofProposal.matches.length === 0}>
                <summary className="text-sm cursor-pointer text-secondary-text">
                  Escolher as faturas à mão ({proofProposal.payable.length} por pagar)
                </summary>
                {/* Procurar é obrigatório e não um extra: por pagar há mais de cem faturas, e
                    encontrar duas à vista desarmada não é trabalho que se peça a ninguém. */}
                <input
                  type="text"
                  value={proofSearch}
                  onChange={(event) => setProofSearch(event.target.value)}
                  placeholder="Procurar por fornecedor ou nº de documento…"
                  className="mt-2 w-full px-3 py-2 border border-border bg-background text-foreground text-sm"
                  style={{ borderRadius: "var(--radius-button)" }}
                />
                {/* Tecto em estilo inline: `max-h-64` também não existe no index.css compilado. */}
                <ul
                  className="mt-2 space-y-1 overflow-y-auto"
                  style={{ maxHeight: 260 }}
                >
                  {proofPayableVisible.length === 0 && (
                    <li className="text-sm text-secondary-text py-2">
                      Nenhuma fatura por pagar corresponde a «{proofSearch}».
                    </li>
                  )}
                  {proofPayableVisible.map((invoice) => (
                    <li key={invoice.invoiceId}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={proofChosen.includes(invoice.invoiceId)}
                          onChange={() => toggleProofInvoice(invoice.invoiceId)}
                        />
                        <span>
                          {invoice.supplierName ?? "—"}
                          {invoice.documentNumber && ` · ${invoice.documentNumber}`}
                          {" · "}
                          {formatCurrency(invoice.outstanding)}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </details>

              {/* A conta à vista: o utilizador vê o que vai acontecer antes de confirmar, não
                  depois. Não basta a diferença — dizer "a última fica parcialmente paga" é falso
                  quando o comprovativo não chega sequer à primeira, que foi o que se viu a
                  escolher 43 mil euros de faturas para um comprovativo de 1500. Aqui simula-se a
                  mesma distribuição que o servidor faz (pela ordem escolhida, sem nunca pagar a
                  mais do que cada fatura deve) e conta-se o desfecho. */}
              {proofChosen.length > 0 && (
                <div className="text-sm border-t border-border pt-3">
                  {(() => {
                    const escolhidas = proofChosen
                      .map((id) =>
                        proofProposal.payable.find((invoice) => invoice.invoiceId === id),
                      )
                      .filter((invoice): invoice is PaymentProofInvoice => invoice !== undefined);

                    const escolhido = escolhidas.reduce(
                      (soma, invoice) => soma + invoice.outstanding,
                      0,
                    );
                    const comprovativo = proofProposal.proofAmount ?? 0;

                    let sobra = comprovativo;
                    let inteiras = 0;
                    let parcial: { nome: string; aplicado: number; total: number } | null = null;
                    let porPagar = 0;

                    for (const invoice of escolhidas) {
                      if (sobra <= 0) {
                        porPagar += 1;
                        continue;
                      }
                      if (sobra >= invoice.outstanding) {
                        sobra -= invoice.outstanding;
                        inteiras += 1;
                        continue;
                      }
                      parcial = {
                        nome: invoice.supplierName ?? "esta fatura",
                        aplicado: sobra,
                        total: invoice.outstanding,
                      };
                      sobra = 0;
                    }

                    const partes: string[] = [];
                    if (inteiras > 0) {
                      partes.push(
                        inteiras === 1 ? "paga 1 fatura por inteiro" : `paga ${inteiras} faturas por inteiro`,
                      );
                    }
                    if (parcial) {
                      partes.push(
                        `deixa ${parcial.nome} com ${formatCurrency(parcial.aplicado)} de ${formatCurrency(parcial.total)}`,
                      );
                    }
                    if (porPagar > 0) {
                      partes.push(
                        porPagar === 1 ? "e 1 fica por pagar" : `e ${porPagar} ficam por pagar`,
                      );
                    }

                    return (
                      <>
                        <div>
                          Selecionado: <strong>{formatCurrency(escolhido)}</strong>{" · "}
                          Comprovativo: <strong>{formatCurrency(comprovativo)}</strong>
                        </div>
                        {sobra > 0 && (
                          <div style={{ color: "var(--destructive)" }}>
                            Sobram {formatCurrency(sobra)} do comprovativo por aplicar — escolha
                            mais faturas ou o valor fica por usar.
                          </div>
                        )}
                        {partes.length > 1 && (
                          <div style={{ color: "var(--destructive)" }}>
                            O comprovativo não chega para o que escolheu: {partes.join(", ")}.
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 border border-border rounded text-foreground"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={() => setProofProposal(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-primary text-primary-foreground rounded disabled:opacity-50"
                style={{ borderRadius: "var(--radius-button)" }}
                disabled={proofBusy || proofChosen.length === 0}
                onClick={confirmProof}
              >
                {/* O valor e não a contagem: com o comprovativo a não chegar para tudo o que foi
                    escolhido, "Confirmar 4 faturas" contradizia a linha logo acima, que dizia que
                    duas delas não recebiam nada. O que se aplica é o dinheiro. */}
                {/* O que chega mesmo às faturas, que pode ser menos do que o comprovativo quando
                    o escolhido não o esgota — anunciar o total do comprovativo contradizia a linha
                    logo acima, que avisa do que sobra por aplicar. */}
                {proofBusy
                  ? "A aplicar…"
                  : `Aplicar ${formatCurrency(
                      Math.min(
                        proofProposal.proofAmount ?? 0,
                        proofProposal.payable
                          .filter((invoice) => proofChosen.includes(invoice.invoiceId))
                          .reduce((soma, invoice) => soma + invoice.outstanding, 0),
                      ),
                    )}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          onMouseDown={pressBackdrop}
          onClick={(event) => {
            if (releasedOnBackdrop(event)) setIsModalOpen(false);
          }}
        >
          <div
            className="bg-card rounded shadow-lg w-full max-h-[90vh] overflow-auto"
            style={{ borderRadius: "var(--radius)", maxWidth: "64rem" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-foreground">
                {editingId != null ? "Editar fatura" : "Nova fatura"}
              </h2>
              <Tooltip text="Fechar">
                <button
                  className="p-1 rounded hover:bg-accent/30 text-secondary-text"
                  onClick={() => setIsModalOpen(false)}
                >
                  <Dismiss20Regular className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {invoiceFormFields}
            </div>

            {formError && (
              <div
                className="px-5 pb-2 text-sm"
                style={{ color: "var(--destructive)" }}
              >
                {formError}
              </div>
            )}

            <div className="px-5 py-4 border-t border-border flex justify-end gap-3">
              <button
                className="px-4 py-2 border border-border rounded text-foreground"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={() => setIsModalOpen(false)}
                disabled={isSaving}
              >
                Cancelar
              </button>
              {editingInvoicePending && (
                <button
                  className="px-4 py-2 border border-border rounded bg-background hover:bg-accent/30 disabled:opacity-50"
                  style={{ borderRadius: "var(--radius-button)", color: "#15803d" }}
                  onClick={() => void handleSave(true)}
                  disabled={isSaving}
                  title="Guarda as alterações e confirma a fatura: sai das por validar"
                >
                  {isSaving ? "A guardar..." : "Guardar e validar"}
                </button>
              )}
              <button
                className="px-4 py-2 bg-primary text-primary-foreground rounded"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={() => void handleSave()}
                disabled={isSaving}
              >
                {isSaving ? "A guardar..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewerDocument && (
        <DocumentViewer
          document={viewerDocument}
          onClose={() => setViewerDocument(null)}
        />
      )}

      {review && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          // zIndex acima de 50: o botão flutuante do assistente de ajuda é z-50 e, por vir
          // depois no DOM da shell, tapava o botão Guardar no canto inferior direito.
          style={{
            backgroundColor: "rgba(0,0,0,0.4)",
            padding: "0.75rem",
            zIndex: 60,
          }}
          onMouseDown={pressBackdrop}
          onClick={(event) => {
            if (releasedOnBackdrop(event)) closeReview();
          }}
        >
          <div
            className="bg-card shadow-lg w-full overflow-hidden flex flex-col"
            style={{
              borderRadius: "var(--radius)",
              width: "97vw",
              maxWidth: "97vw",
              height: "95vh",
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">
                  Rever registo do documento
                </h2>
                <p className="text-sm text-secondary-text break-all">
                  {review.invoice.documentFileName ?? "Documento"} · confira e
                  ajuste os dados lidos antes de guardar
                </p>
              </div>
              <Tooltip text="Fechar">
                <button
                  className="p-1 rounded hover:bg-accent/30 text-secondary-text"
                  onClick={closeReview}
                >
                  <Dismiss20Regular className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>

            <div className="flex" style={{ flex: 1, minHeight: 0 }}>
              <div
                className="hidden md:block"
                style={{
                  flex: 1,
                  minWidth: 0,
                  borderRight: "1px solid var(--border)",
                  backgroundColor: "var(--muted)",
                }}
              >
                {review.isPdf ? (
                  <iframe
                    title="Pré-visualização do documento"
                    // #navpanes=0 esconde o painel lateral de miniaturas das páginas.
                    src={`${review.url}#navpanes=0`}
                    style={{ width: "100%", height: "100%", border: "none" }}
                  />
                ) : isSpreadsheetName(review.invoice.documentFileName) ? (
                  <div
                    className="flex flex-col items-center justify-center gap-2 text-center p-6 text-secondary-text text-sm"
                    style={{ width: "100%", height: "100%" }}
                  >
                    <span style={{ fontWeight: "var(--font-weight-medium)" }}>
                      Folha de cálculo sem pré-visualização
                    </span>
                    <span>
                      Confira os dados lidos no formulário ao lado; para ver o
                      ficheiro, descarregue-o nos documentos da fatura.
                    </span>
                  </div>
                ) : (
                  <div
                    className="overflow-auto flex items-start justify-center"
                    style={{ width: "100%", height: "100%" }}
                  >
                    <img
                      src={review.url}
                      alt="Pré-visualização do documento"
                      style={{ maxWidth: "100%" }}
                    />
                  </div>
                )}
              </div>

              <div
                className="overflow-auto"
                style={{ width: "32rem", maxWidth: "100%", flexShrink: 0 }}
              >
                {/* flex-col e não grid: os campos partilhados trazem `md:col-span-2`, que numa
                    grelha de 1 coluna criaria uma segunda coluna implícita e espremia tudo a
                    meio. Em flex esse span é inerte e os campos ficam mesmo todos na vertical. */}
                <div className="p-5 flex flex-col gap-4">
                  {invoiceFormFields}
                </div>
                {formError && (
                  <div
                    className="px-5 pb-2 text-sm"
                    style={{ color: "var(--destructive)" }}
                  >
                    {formError}
                  </div>
                )}
              </div>
            </div>

            {/* O registo JÁ foi criado (por validar) quando o documento foi processado — a janela
                só revê. Os três desfechos ficam explícitos: eliminar, rever mais tarde, guardar.
                "Cancelar" mentia: fechava a janela mas a fatura ficava importada na mesma. */}
            <div className="px-5 py-4 border-t border-border flex items-center gap-3">
              <button
                className="px-4 py-2 border rounded"
                style={{
                  borderRadius: "var(--radius-button)",
                  color: "var(--destructive)",
                  borderColor: "var(--destructive)",
                }}
                onClick={async () => {
                  if (await handleDelete(review.invoice)) closeReview();
                }}
                disabled={isSaving}
              >
                Eliminar registo
              </button>
              <div className="ml-auto flex items-center gap-3">
                <Tooltip text="Fecha a revisão; o registo fica na lista com os dados lidos.">
                  <button
                    className="px-4 py-2 border border-border rounded text-foreground"
                    style={{ borderRadius: "var(--radius-button)" }}
                    onClick={closeReview}
                    disabled={isSaving}
                  >
                    Rever mais tarde
                  </button>
                </Tooltip>
                {editingInvoicePending && (
                  <button
                    className="px-4 py-2 border border-border rounded bg-background hover:bg-accent/30 disabled:opacity-50"
                    style={{ borderRadius: "var(--radius-button)", color: "#15803d" }}
                    onClick={() => void handleSave(true)}
                    disabled={isSaving}
                    title="Guarda as alterações e confirma a fatura: sai das por validar"
                  >
                    {isSaving ? "A guardar..." : "Guardar e validar"}
                  </button>
                )}
                <button
                  className="px-4 py-2 bg-primary text-primary-foreground rounded"
                  style={{ borderRadius: "var(--radius-button)" }}
                  onClick={() => void handleSave()}
                  disabled={isSaving}
                >
                  {isSaving ? "A guardar..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {docInvoice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.4)" }}
          onMouseDown={pressBackdrop}
          onClick={(event) => {
            if (releasedOnBackdrop(event)) setDocInvoice(null);
          }}
        >
          <div
            className="bg-card rounded shadow-lg w-full max-h-[90vh] overflow-auto"
            style={{ borderRadius: "var(--radius)", maxWidth: "72rem" }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-4">
              <div>
                <h2
                  ref={docModalHeadingRef}
                  tabIndex={-1}
                  className="text-lg font-semibold text-foreground inline-flex items-center gap-2"
                >
                  Documentos da fatura
                  <CicloPill fase={docInvoice.lifecyclePhase} />
                </h2>
                <p className="text-sm text-secondary-text">
                  {docInvoice.matchedSupplierName ??
                    docInvoice.supplierName ??
                    "Fornecedor"}
                  {docInvoice.documentNumber
                    ? ` · ${docInvoice.documentNumber}`
                    : ""}
                </p>
              </div>
              <Tooltip text="Fechar">
                <button
                  className="p-1 rounded hover:bg-accent/30 text-secondary-text"
                  onClick={() => setDocInvoice(null)}
                >
                  <Dismiss20Regular className="w-5 h-5" />
                </button>
              </Tooltip>
            </div>

            <div className="px-5 py-4 border-b border-border bg-muted grid grid-cols-2 md:grid-cols-3 gap-4">
              <DetailField
                label="Categoria"
                value={categoryLabel(docInvoice)}
              />
              <DetailField
                label="Obra"
                value={
                  docInvoice.isSplit
                    ? splitWorkLabel(docInvoice)
                    : (docInvoice.workName ?? "—")
                }
              />
              <DetailField
                label="Data do documento"
                value={formatDate(docInvoice.documentDate)}
              />
              <DetailField
                label="Nº documento"
                value={docInvoice.documentNumber ?? "—"}
              />
              <DetailField label="Estado" value={docInvoice.approvalStatusName} />
              <DetailField
                label="Valor c/IVA"
                value={formatCurrency(docInvoice.totalAmount)}
              />
              <DetailField
                label="Valor pago"
                value={
                  docInvoice.paidAmount != null
                    ? formatCurrency(docInvoice.paidAmount)
                    : "—"
                }
              />
            </div>

            {/* NC: uma nota de crédito não é uma compra, e uma despesa anulada foi desfeita —
                ambas ficam fora do Resumo Financeiro. A deteção automática vai pelo nº do
                documento ("NC …"); aqui corrige-se à mão o que ela não apanhou. */}
            <div aria-busy={savingCreditNote} className="px-5 py-3 border-b border-border text-sm space-y-2">
              <div>
                <label className="inline-flex items-center gap-2">
                  <input
                    ref={creditNoteCheckboxRef}
                    type="checkbox"
                    checked={docInvoice.isCreditNote ?? false}
                    disabled={savingCreditNote}
                    onChange={(event) => void handleToggleCreditNote(event.target.checked)}
                  />
                  Esta linha é uma nota de crédito (fica fora do Resumo)
                </label>
              </div>
              <div>
                <label className="inline-flex items-center gap-2">
                  <input
                    ref={annulCheckboxRef}
                    type="checkbox"
                    checked={docInvoice.isAnnulledByCreditNote ?? false}
                    disabled={savingCreditNote}
                    onChange={(event) => void handleSetAnnulment(event.target.checked)}
                  />
                  Anulada por uma nota de crédito (fica fora do Resumo)
                </label>
              </div>
              <div>
                <label className="inline-flex items-center gap-2">
                  <input
                    ref={summaryExclusionCheckboxRef}
                    type="checkbox"
                    checked={docInvoice.excludeFromSummary ?? false}
                    disabled={savingCreditNote}
                    onChange={(event) => void handleSetSummaryExclusion(event.target.checked)}
                  />
                  Não contar esta linha no Resumo do ano
                </label>
              </div>
              {/* Região viva sempre montada: o texto muda para os leitores anunciarem o guardar. */}
              <div aria-live="polite" className="text-secondary-text text-xs">
                {savingCreditNote ? "A guardar…" : ""}
              </div>
              {docInvoice.isAnnulledByCreditNote && (
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="nc-reference" className="text-secondary-text">
                    Referência da NC
                  </label>
                  <input
                    id="nc-reference"
                    value={annulmentReference}
                    maxLength={64}
                    onChange={(event) => setAnnulmentReference(event.target.value)}
                    placeholder="NC 4/10125"
                    className="border border-border px-2 py-1 bg-card text-foreground"
                  />
                  <button
                    type="button"
                    disabled={savingCreditNote}
                    onClick={() => void handleSetAnnulment(true)}
                    className="h-8 px-3 border border-border bg-card hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    Guardar referência
                  </button>
                </div>
              )}
            </div>

            {docInvoice.sourceChannel === "email" && (
              <div className="px-5 py-3 border-b border-border text-sm text-secondary-text">
                ✉ Recebido por email
                {docInvoice.sourceSender ? ` de ${docInvoice.sourceSender}` : ""}
                {docInvoice.sourceSubject ? ` · "${docInvoice.sourceSubject}"` : ""}
                {docInvoice.sourceReceivedAt
                  ? ` · ${formatDate(docInvoice.sourceReceivedAt)}`
                  : ""}
              </div>
            )}

            {docInvoice.reappliedFromPreviousReview && (
              <div
                className="px-5 py-3 border-b border-border text-sm"
                style={{ color: "#b45309", backgroundColor: "rgba(245,158,11,0.10)" }}
              >
                ↺ Distribuição reaplicada de uma revisão anterior — este documento já tinha
                entrado e sido confirmado; verifique se não é um duplicado.
              </div>
            )}

            {/* Comprovativo com mais do que a fatura: ou paga várias faturas, ou está na fatura
                errada. O estado fica por decidir — é a pessoa que sabe qual dos dois é. */}
            {docInvoice.paidAmount != null &&
              docInvoice.totalAmount != null &&
              docInvoice.paidAmount > docInvoice.totalAmount && (
                <div
                  className="px-5 py-3 border-b border-border text-sm"
                  style={{ color: "#b45309", backgroundColor: "rgba(245,158,11,0.10)" }}
                >
                  ⚠ O comprovativo tem {formatCurrency(docInvoice.paidAmount)} e esta fatura é de{" "}
                  {formatCurrency(docInvoice.totalAmount)} — confirme se o pagamento cobre várias
                  faturas ou se o ficheiro é desta despesa.
                </div>
              )}

            {docInvoice.isSplit && (
              <div className="px-5 py-4 border-b border-border">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground">
                    Repartição por obra ({docInvoice.allocations.length})
                  </span>
                  <span
                    className="text-xs"
                    style={{
                      color:
                        docInvoice.imputedCostCount > 0
                          ? "var(--primary)"
                          : "var(--secondary-text)",
                    }}
                  >
                    {docInvoice.imputedCostCount > 0
                      ? `Imputado · ${formatCurrency(docInvoice.imputedAmount)}`
                      : "Por imputar"}
                  </span>
                </div>
                <div className="flex flex-col gap-1" style={{ marginTop: "0.5rem" }}>
                  {docInvoice.allocations.map((allocation) => (
                    <AllocationSummaryRow
                      key={allocation.id}
                      allocation={allocation}
                      works={works}
                      onFeedbackSaved={applyAllocationFeedback}
                      onFeedbackError={setDocError}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* A banda do meio do casamento com previsões: mostra-se, não se decide. Cada cartão
                é a previsão como o utilizador a escreveu — texto, valor e parecença por palavras. */}
            {(docInvoice.forecastSuggestions?.length ?? 0) > 0 && (
              <div className="px-5 pt-4 flex flex-col gap-2">
                <div className="text-sm text-secondary-text">
                  {docInvoice.documentIsAuto &&
                  docInvoice.forecastSuggestions!.some((suggestion) => suggestion.hasInvoice)
                    ? "Este auto pode ser de uma fatura que já entrou — juntar põe o auto nos autos e anexos dela, numa só linha:"
                    : "Esta fatura pode ser uma despesa que já previu — fundir junta as duas numa só linha, guardando o valor previsto:"}
                </div>
                <ul className="flex flex-col gap-2">
                  {docInvoice.forecastSuggestions!.map((suggestion) => (
                    <li
                      key={suggestion.forecastEntryId}
                      className="flex items-center gap-3 p-3 border border-border"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <span
                        className="text-xs flex-shrink-0"
                        style={{
                          padding: "1px 6px",
                          borderRadius: "var(--radius-button)",
                          backgroundColor: "var(--accent)",
                          color: "var(--foreground)",
                          fontWeight: "var(--font-weight-medium)",
                        }}
                      >
                        {suggestion.score >= 0.9
                          ? "Muito parecida"
                          : suggestion.score >= 0.7
                            ? "Parecida"
                            : "Pode ser"}
                      </span>
                      <div className="flex flex-col min-w-0 text-sm">
                        <span className="text-foreground break-words">
                          {suggestion.description ?? suggestion.supplierName ?? "Despesa prevista"}
                        </span>
                        <span className="text-secondary-text" style={{ fontSize: "var(--text-xs)" }}>
                          {suggestion.supplierName ?? "—"}
                          {suggestion.forecastAmount != null &&
                            ` · ${suggestion.hasInvoice ? "fatura" : "previsto"} ${formatCurrency(suggestion.forecastAmount)}`}
                          {suggestion.documentNumber && ` · ${suggestion.documentNumber}`}
                          {suggestion.documentDate && ` · ${formatDate(suggestion.documentDate)}`}
                        </span>
                        {/* A obra da outra linha: os autos de um sub-empreiteiro dizem todos o mesmo,
                            e sem ela não se sabia de que trabalho era. */}
                        <span className="text-secondary-text break-words" style={{ fontSize: "var(--text-xs)" }}>
                          {`Obra: ${forecastSuggestionWorks(suggestion.works)}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="ml-auto flex-shrink-0 px-3 py-1.5 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50"
                        style={{ borderRadius: "var(--radius-button)" }}
                        disabled={mergingForecastId !== null}
                        aria-busy={mergingForecastId === suggestion.forecastEntryId}
                        aria-label={`${suggestion.hasInvoice ? "Juntar à fatura" : "Fundir com a previsão"}: ${
                          suggestion.description ?? suggestion.supplierName ?? "despesa prevista"
                        }${
                          suggestion.forecastAmount != null
                            ? `, ${formatCurrency(suggestion.forecastAmount)}`
                            : ""
                        }`}
                        onClick={() =>
                          mergeForecast(docInvoice.id, suggestion.forecastEntryId, suggestion.hasInvoice === true)
                        }
                      >
                        {mergingForecastId === suggestion.forecastEntryId
                          ? suggestion.hasInvoice
                            ? "A juntar…"
                            : "A fundir…"
                          : suggestion.hasInvoice
                            ? "Juntar a esta"
                            : "Fundir com esta"}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* O rasto da fusão: o que o utilizador previu, e a porta de saída. */}
            {docInvoice.forecastedAmount != null && (
              <div className="px-5 pt-4">
                <div
                  className="flex items-center gap-3 p-3 border border-border text-sm"
                  style={{ borderRadius: "var(--radius)" }}
                >
                  <span>
                    Esta despesa nasceu de uma previsão de{" "}
                    <strong>{formatCurrency(docInvoice.forecastedAmount)}</strong>, completada pela
                    fatura de <strong>{formatCurrency(docInvoice.totalAmount ?? 0)}</strong>.
                  </span>
                  <button
                    type="button"
                    className="ml-auto flex-shrink-0 px-3 py-1.5 text-sm border border-border bg-background text-foreground hover:bg-accent/30 disabled:opacity-50"
                    style={{ borderRadius: "var(--radius-button)" }}
                    disabled={unmergingForecast}
                    aria-busy={unmergingForecast}
                    onClick={undoForecastMerge}
                  >
                    {unmergingForecast ? "A desfazer…" : "Desfazer a fusão"}
                  </button>
                </div>
              </div>
            )}

            {/* Três caixas independentes, na ordem Auto → Fatura → Comprovativo. O auto tem casa
                própria (EntityDocument), por isso já não disputa a ranhura com a fatura: pode haver
                as três ao mesmo tempo. */}
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
              <UploadBox
                title="Auto"
                icon={<DocumentTable20Regular className="w-5 h-5" />}
                hasFile={autoDocs.length > 0 || docInvoice.documentIsAuto}
                fileName={
                  autoDocs[0]?.fileName ??
                  (docInvoice.documentIsAuto
                    ? docInvoice.documentFileName
                    : null)
                }
                subtitle={
                  autoDocs.length > 1
                    ? `${autoDocs.length} ficheiros — mostra o mais recente`
                    : autoDocs.length === 0 && docInvoice.documentIsAuto
                      ? "Documento que deu origem a esta despesa."
                      : null
                }
                emptyHint="O auto de medição que justifica a despesa. Aceita PDF ou Excel."
                isUploading={uploadingKind === "auto"}
                accept={EXPENSE_DOCUMENT_ACCEPT}
                onDownload={() => {
                  const doc = autoDocs[0];
                  if (doc) {
                    window.open(
                      getEntityDocumentFileUrl(doc.id),
                      "_blank",
                      "noopener",
                    );
                  } else {
                    runDownload(downloadInvoiceDocument(docInvoice), setDocError);
                  }
                }}
                onView={
                  isSpreadsheetName(
                    autoDocs[0]?.fileName ?? docInvoice.documentFileName,
                  )
                    ? undefined
                    : () => {
                        const doc = autoDocs[0];
                        setViewerDocument(
                          doc
                            ? {
                                name: doc.fileName,
                                load: () => fetchEntityDocumentFile(doc.id),
                              }
                            : {
                                name: docInvoice.documentFileName ?? "Auto",
                                load: () => fetchInvoiceDocument(docInvoice),
                              },
                        );
                      }
                }
                onSelectFile={(file) => handleAutoUpload(file)}
                onRemove={autoDocs.length > 0 ? handleAutoRemove : undefined}
              />
              <FaturaBox
                files={faturaFiles}
                isUploading={uploadingKind === "document"}
                accept={EXPENSE_DOCUMENT_ACCEPT}
                emptyHint={
                  docInvoice.documentIsAuto
                    ? "O documento que deu origem a esta despesa é um auto (na caixa Auto). Junte aqui a fatura do fornecedor quando ela chegar."
                    : null
                }
                onSelectFile={(file) => handleFaturaUpload(file)}
              />
              <UploadBox
                title="Comprovativo"
                icon={<Receipt20Regular className="w-5 h-5" />}
                hasFile={docInvoice.hasPaymentProof}
                fileName={docInvoice.paymentProofFileName}
                subtitle={
                  docInvoice.paymentProofs.length > 0
                    ? // Uma linha pode ter vários comprovativos — o valor pago é a soma. Clicar/arrastar
                      // acrescenta outro; a lista em baixo mostra-os todos.
                      `${docInvoice.paymentProofs.length} comprovativo${
                        docInvoice.paymentProofs.length === 1 ? "" : "s"
                      }${
                        docInvoice.paidAmount != null
                          ? ` · ${formatCurrency(docInvoice.paidAmount)} pagos`
                          : ""
                      } · clique para adicionar outro`
                    : null
                }
                isUploading={uploadingKind === "proof"}
                onDownload={() =>
                  runDownload(
                    downloadInvoicePaymentProof(docInvoice),
                    setDocError,
                  )
                }
                onView={() =>
                  setViewerDocument({
                    name: docInvoice.paymentProofFileName ?? "Comprovativo",
                    load: () => fetchInvoicePaymentProof(docInvoice),
                  })
                }
                onSelectFile={(file) => handleDocUpload("proof", file)}
              />
            </div>

            {/* Comprovativos da linha: uma despesa pode ter vários (pagamentos parciais, um por
                fatura). O valor pago é a soma dos montantes; o estado (Pago / Parcialmente pago)
                deriva daí. */}
            {docInvoice.paymentProofs.length > 0 && (
              <div className="px-5 pb-5">
                <div
                  ref={proofListHeadingRef}
                  tabIndex={-1}
                  role="status"
                  className="text-sm font-medium text-foreground mb-2"
                >
                  Comprovativos desta linha ({docInvoice.paymentProofs.length})
                  {docInvoice.paidAmount != null
                    ? ` · ${formatCurrency(docInvoice.paidAmount)} pagos`
                    : ""}
                </div>
                <div className="flex flex-col gap-1" role="list">
                  {docInvoice.paymentProofs.map((proof) => (
                    <div
                      key={proof.id}
                      role="listitem"
                      className="flex items-center justify-between gap-3 px-3 py-2 border border-border rounded"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Receipt20Regular className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm text-foreground truncate">
                          {proof.fileName}
                        </span>
                        <span className="text-sm text-secondary-text flex-shrink-0">
                          {proof.amount != null
                            ? formatCurrency(proof.amount)
                            : "sem montante lido"}
                        </span>
                      </span>
                      <span className="flex items-center gap-3 flex-shrink-0">
                        <button
                          type="button"
                          aria-label={`Ver ${proof.fileName}`}
                          disabled={uploadingKind === "proof"}
                          className="inline-flex items-center gap-1 text-sm hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ color: "var(--primary)" }}
                          onClick={() =>
                            setViewerDocument({
                              name: proof.fileName ?? "Comprovativo",
                              load: () => fetchPaymentProof(proof),
                            })
                          }
                        >
                          <Eye20Regular className="w-4 h-4" aria-hidden="true" />
                          Ver
                        </button>
                        <button
                          type="button"
                          aria-label={`Remover ${proof.fileName}`}
                          disabled={uploadingKind === "proof"}
                          className="inline-flex items-center gap-1 text-sm hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{ color: "var(--destructive)" }}
                          onClick={() => handleRemoveProof(proof.id)}
                        >
                          <Delete20Regular className="w-4 h-4" aria-hidden="true" />
                          Remover
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {docError && (
              <div
                role="alert"
                className="px-5 pb-2 text-sm"
                style={{ color: "var(--destructive)" }}
              >
                {docError}
              </div>
            )}

            {docNotice && (
              <div
                className="px-5 pb-2 text-sm"
                style={{ color: "var(--warning, #b45309)" }}
              >
                {docNotice}
              </div>
            )}

            <div className="px-5 py-4 border-t border-border flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded text-foreground hover:bg-accent/30"
                  style={{ borderRadius: "var(--radius-button)" }}
                  onClick={editFromDocuments}
                >
                  <Edit20Regular className="w-5 h-5" />
                  Editar
                </button>
                <button
                  className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded hover:bg-accent/30"
                  style={{
                    borderRadius: "var(--radius-button)",
                    color: "var(--destructive)",
                  }}
                  onClick={() => handleDelete(docInvoice)}
                >
                  <Delete20Regular className="w-5 h-5" />
                  Excluir
                </button>
              </div>
              <button
                className="px-4 py-2 border border-border rounded text-foreground"
                style={{ borderRadius: "var(--radius-button)" }}
                onClick={() => setDocInvoice(null)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
